import store from '@/store';
import workflowService from '../workflowService';
import apiService from '../apiService';
import bundleOptionService from './bundleOptionService';
import { dropEfficacyClaims, dropEfficacyClaimTags, isMedicalCategory } from './seoEfficacyGuard';
import { logAIRequestError } from './errorLoggingService';
import { dropSpecsNotInOptions, dropPartialOptionParens, summarizeOptionValues, isKidsCategory, stripKidsWords, dropAudienceNotInFeature, dropCountsNotInSource, markCapacityCounts, dropShapeWordsNotInOptions, dropItemCodes, hasHan, stripHan, dropConflictingItemWords, dropBannedFeatureWords, bannedPhrasesFromFeature, dropUnsupportedClaims, dropUnsupportedTags, ensureBundleCount } from './seoSpecGuard';
import { dropOriginClaims, dropOriginClaimTags, dropKidsWordsNotInSource, isChinaSourced } from './seoOriginGuard';
import { dropColorsNotInOptions, dropLayerWordsNotInOptions, fixOptionTerms, dropUnsupportedOptionClaims } from './seoSpecGuard';
import { recordKorNames, keepUserKorNames, restoreUserKorNames, isUserKorName, userKorNameOf } from './userEditGuard';

/**
 * SEO 설정을 위한 서비스
 */
const seoService = {
  /**
   * 검색태그로 쓸 키워드 정리 — 쿠팡은 허용 안 되는 특수기호·오타(자모만)가 섞이면 등록을 반려한다.
   * 근거: 쿠팡 도움말 「[로켓배송][검색태그] 반려」(couplus-cli/skills/management/wing-help/references/로켓배송.md)
   *   - 태그당 공백 포함 20자 이내, 전체 150자 이내, 쉼표로 구분(빈 태그 금지)
   *   - 일부 특수기호만 허용 — 어느 것이 막히는지 확정 못 해 보수적으로 한글(완성형)·영문·숫자·공백만 남긴다
   * @param {Array} keywords
   * @returns {string[]} 정리된 태그(중복·빈 값·20자 초과 제거)
   */
  cleanSearchTagKeywords(keywords) {
    if (!Array.isArray(keywords)) return [];
    const out = [];
    for (const raw of keywords) {
      const text = String(raw === undefined || raw === null ? '' : raw);
      if (/[ㄱ-ㅎㅏ-ㅣ]/.test(text)) continue;   // 자모만 있는 글자 = 오타로 반려된다(예: ㅋㅜ팡)
      const tag = text
        .replace(/[^가-힣A-Za-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!tag || tag.length > 20 || out.includes(tag)) continue;
      out.push(tag);
    }
    if (out.length !== keywords.length) {
      console.log(`검색태그 정리: ${keywords.length}개 → ${out.length}개 (허용 안 되는 문자·20자 초과·중복 제외)`);
    }
    return out;
  },

  /** 묶음 수량 N — 소싱 묶음(sourcingBundleQuantity) 먼저, 없으면 옵션 bundleQuantity 가 전부 같은 ≥2 값일 때만(다르면 없음 · 지어내지 않음) */
  bundleQuantityOf(product) {
    return bundleOptionService.productBundleQuantity(product);   // 규칙 한 곳(허브·라벨 이름도 같은 값으로 가른다)
  },

  /**
   * 등록 카테고리 이름 경로. 로켓 등록은 쿠팡 문서 startPage.categoryPath 에 «A>B>C (번호)» 로 있다(rocketRegInfo 에는 없다).
   * 뒤의 «(번호)» 는 뗀다 — 이름 없이 « (번호)» 만 있으면 빈 값(모름)이 된다.
   */
  categoryPathOf(product) {
    if (!product) return '';
    const docs = (product.coupangDoc && product.coupangDoc.jsonDocument) || [];
    const fromDoc = docs.length && docs[0] && docs[0].startPage ? docs[0].startPage.categoryPath : '';
    const raw = (product.rocketRegInfo && product.rocketRegInfo.categoryPath) || product.categoryPath || fromDoc || '';
    return String(raw).replace(/\s*\(\d+\)\s*$/, '').trim();
  },

  /**
   * 1688(중국 공급처) 상품의 상품명·검색태그에서 원산지 한국 주장(국산·국내산·한국산·메이드인코리아)을 빼고 경고(seoWarning)를 단다.
   * 쿠팡 연관 키워드(«국산광목주머니»)·1688 문구(国产=중국산)를 AI 가 옮겨 와 원산지 오표시가 된다(PR261017-2A7068).
   */
  applyOriginGuard(product, seoData, productFeature) {
    const src = { attributes: product.attributes, productFeature, originalTitle: product.productName, purchaseLink: product['구매링크'] };
    // 근거 없는 아동 대상(«여아» 등) — 카테고리 이름을 몰라 stripKidsWords 가 못 볼 때(PR261017-3BB158)
    const kids = dropKidsWordsNotInSource(seoData.productName, { ...src, options: product.options, targetKeyword: product.targetKeyword }, { allowEmpty: true });
    if (kids.removed.length && !kids.name) throw this.factEmptyError(`상품명 «${seoData.productName}»`, kids.removed);
    if (kids.removed.length) {
      console.warn(`근거 없는 아동 대상 낱말 제거: "${seoData.productName}" > "${kids.name}"`);
      seoData.productName = kids.name;
      this.addSeoWarning(product, seoData, 'kidsNoSource', kids.removed,
        `1688 원래 제목·속성·옵션·상품 특징에 아동 대상이 없어 상품명에서 «${kids.removed.join('·')}»를 뺐습니다(쿠팡 연관 키워드의 낱말). 아동용 상품이면 상품 특징에 적고 다시 만드세요.`);
    }
    const nm = dropOriginClaims(seoData.productName, src, { allowEmpty: true });
    if (nm.removed.length && !nm.name) throw this.factEmptyError(`상품명 «${seoData.productName}»`, nm.removed);
    const tg = dropOriginClaimTags(seoData.keywords, src);
    if (tg.removed.length) seoData.keywords = tg.keywords;
    if (!nm.removed.length && !tg.removed.length) return;
    if (nm.removed.length) {
      console.warn(`원산지 한국 주장 제거: "${seoData.productName}" > "${nm.name}"`);
      seoData.productName = nm.name;
    }
    const words = [...new Set([...nm.removed, ...tg.removed].map(w => String(w).trim()))];
    this.addSeoWarning(product, seoData, 'originClaim', words,
      `1688(중국 공급처) 상품이라 상품명·검색태그의 «${words.slice(0, 5).join('·')}»(원산지 한국 주장)를 뺐습니다. 원산지는 중국입니다. 실제 한국산이면 상품 특징에 적고 다시 만드세요.`);
  },

  /**
   * 어린이용이 아닌 카테고리인데 상품명에 «어린이·아동·키즈·유아»가 들어가면 빼고 경고(seoWarning)를 단다.
   * 1688 원문·연관 키워드에서 옮겨 와 일반 상품이 어린이제품(KC 인증 대상)처럼 보인다(오픈이슈 #144 · PR261016-89F9B8).
   */
  applyKidsWordGuard(product, seoData, productFeature, targetKeyword) {
    // 상품 특징·대상 키워드가 한쪽 대상(성인/아동)만 말하면 다른 쪽 대상 낱말을 뺀다 — 카테고리를 몰라도(PR261017-19D39A · kidswordbaby-1)
    const aud = dropAudienceNotInFeature(seoData.productName, productFeature, targetKeyword);
    if (aud.removed.length) {
      console.warn(`상품 특징과 다른 대상 낱말 제거: "${seoData.productName}" > "${aud.name}"`);
      seoData.productName = aud.name;
      this.addSeoWarning(product, seoData, 'audience', aud.removed,
        `상품 특징에 적힌 대상과 달라서 상품명에서 «${aud.removed.join('·')}»를 뺐습니다(1688 원래 제목의 문구). 실제로 그 대상도 쓰는 상품이면 상품 특징에 적고 다시 만드세요.`);
    }
    const categoryPath = this.categoryPathOf(product);
    const { name, removed } = stripKidsWords(seoData.productName, categoryPath);
    if (!removed.length) return;
    console.warn(`어린이용 낱말 제거(카테고리 ${categoryPath}): "${seoData.productName}" > "${name}"`);
    seoData.productName = name;
    this.addSeoWarning(product, seoData, 'kidsWords', removed,
      `어린이용 카테고리가 아니어서 상품명에서 «${removed.join('·')}»를 뺐습니다(어린이제품 KC 인증 대상으로 보일 수 있음). 어린이용 상품이면 카테고리를 어린이용으로 바꾸고 상품명을 다시 정하세요.`);
  },

  /**
   * 상품명의 개수(9개입·4구 …)가 남긴 옵션명·상품 특징에 없으면 빼고 경고(seoWarning)를 단다.
   * 1688 원문 제목의 개수를 AI 가 옮겨 와 실제 구성과 다른 개수가 들어간다(PR261016-423097 · PR261016-51A981).
   */
  applyCountGuard(product, seoData, productFeature, targetKeyword) {
    const cap = markCapacityCounts(seoData.productName, product.options, productFeature);
    if (cap.marked.length) {
      console.warn(`수납 용량 표시: "${seoData.productName}" > "${cap.name}"`);
      seoData.productName = cap.name;
    }
    const claim = dropUnsupportedClaims(seoData.productName, { attributes: product.attributes, options: product.options, productFeature, targetKeyword }, { allowEmpty: true });
    if (claim.removed.length && !claim.name) throw this.factEmptyError(`상품명 «${seoData.productName}»`, claim.removed);
    if (claim.removed.length) {
      console.warn(`근거 없는 소재·효능 제거: "${seoData.productName}" > "${claim.name}"`);
      seoData.productName = claim.name;
      this.addSeoWarning(product, seoData, 'unsupportedClaim', claim.removed,
        `상품명의 «${[...new Set(claim.removed.map(w => w.trim()))].join('·')}»는 1688 속성·고른 옵션에 근거가 없어 뺐습니다(원래 제목의 광고 문구). 실제 소재·기능이 맞으면 상품 특징에 적고 다시 만드세요.`);
    }
    const ban = dropBannedFeatureWords(seoData.productName, productFeature, product.attributes, targetKeyword, { allowEmpty: true });
    if (ban.removed.length && !ban.name) throw this.factEmptyError(`상품명 «${seoData.productName}»`, ban.removed);
    if (ban.removed.length) {
      console.warn(`상품 특징에서 금지한 표현 제거: "${seoData.productName}" > "${ban.name}"`);
      seoData.productName = ban.name;
      this.addSeoWarning(product, seoData, 'bannedByFeature', ban.removed,
        `상품 특징에 금지로 적힌 표현 «${[...new Set(ban.removed.map(w => w.trim()))].join('·')}»를 상품명에서 뺐습니다.`);
    }
    // 대상 키워드라 남긴 소재 낱말이 1688 소재와 다르면 지우지 않고 경고만(PR261017-B7DD35: 키워드 «실리콘타이» · 재질 PE)
    if (claim.kept && claim.kept.length) {
      const mat = (product.attributes || []).filter(a => a && /재질|소재|材质|材料|面料/.test(a.attributeName || '')).map(a => a.value).filter(Boolean);
      this.addSeoWarning(product, seoData, 'keywordMaterial', claim.kept,
        `대상 키워드의 «${[...new Set(claim.kept)].join('·')}»는 1688 소재${mat.length ? `(${[...new Set(mat)].join('·')})` : ''}와 다릅니다. 실제 소재를 확인하고 상품명·대상 키워드를 고치세요.`);
    }
    // 검색태그도 상품명과 같은 사실성 검사 — 금지 표현·근거 없는 소재/효능/안전 주장·다른 대상 태그는 뺀다(PR261017-B7DD35 · 48349F)
    if (Array.isArray(seoData.keywords)) {
      const tags = dropUnsupportedTags(seoData.keywords, { attributes: product.attributes, options: product.options, productFeature, targetKeyword, categoryPath: this.categoryPathOf(product) });
      if (tags.removed.length) {
        console.warn(`근거 없는 검색태그 제거: ${tags.removed.join(', ')}`);
        seoData.keywords = tags.keywords;
        this.addSeoWarning(product, seoData, 'tags', tags.removed,
          `검색태그 «${tags.removed.join('·')}»는 상품 특징·1688 속성에 근거가 없거나 금지된 표현이라 뺐습니다.`);
      }
    }
    // 일반 상품의 효능·의료 표현(통증 완화·치료·염증 — «자세 교정» 같은 상품 종류 이름은 남긴다) — 쿠팡 연관 키워드에서 옮겨 온다. 의료기기 카테고리만 남긴다(PR261017-D188A7)
    const efficacyCat = this.categoryPathOf(product);
    const eff = dropEfficacyClaims(seoData.productName, efficacyCat);
    const effTags = dropEfficacyClaimTags(seoData.keywords, efficacyCat);
    if (effTags.removed.length) seoData.keywords = effTags.keywords;
    if (eff.removed.length || effTags.removed.length) {
      if (eff.removed.length) {
        console.warn(`효능·의료 표현 제거: "${seoData.productName}" > "${eff.name}"`);
        seoData.productName = eff.name;
      }
      const words = [...new Set([...eff.removed, ...effTags.removed])];
      this.addSeoWarning(product, seoData, 'efficacyClaim', words,
        `의료기기가 아닌 상품이라 상품명·검색태그에서 효능·의료 표현 «${words.slice(0, 5).join('·')}»를 뺐습니다(쿠팡 연관 키워드의 표현). 의료기기 허가 상품이면 카테고리를 의료기기로 정하고 다시 만드세요.`);
    }
    const item = dropConflictingItemWords(seoData.productName, targetKeyword, product.attributes);
    if (item.removed.length) {
      console.warn(`대상 키워드와 다른 품목 낱말 제거: "${seoData.productName}" > "${item.name}"`);
      seoData.productName = item.name;
      this.addSeoWarning(product, seoData, 'itemType', item.removed,
        `상품명의 «${item.removed.join('·')}»는 대상 키워드(${targetKeyword})와 다른 품목이라 뺐습니다. 상품명을 확인하세요.`);
    }
    const code = dropItemCodes(seoData.productName, product.options, product.attributes);
    if (code.removed.length) {
      console.warn(`공급사 품번 제거: "${seoData.productName}" > "${code.name}"`);
      seoData.productName = code.name;
    }
    this.applyOptionWordGuards(product, seoData);
    const { name, removed } = dropCountsNotInSource(seoData.productName, product.options, productFeature, product.sourcingBundleQuantity);
    if (removed.length) {
      console.warn(`근거 없는 개수 제거: "${seoData.productName}" > "${name}"`);
      seoData.productName = name;
      this.addSeoWarning(product, seoData, 'counts', removed,
        `상품명의 개수 «${removed.join('·')}»가 남긴 옵션·상품 특징에 없어 뺐습니다(1688 원래 제목의 개수일 수 있음). 실제 구성 개수를 상품명에 넣으려면 상품 특징에 적고 다시 만드세요.`);
    }
    // 묶음 상품은 묶음 수를 상품명에 붙인다(F07A65) — AI 가 안 썼을 때만 · 상한 넘으면 안 붙이고 알림
    const bundle = ensureBundleCount(seoData.productName, this.bundleQuantityOf(product));
    if (bundle.added) {
      console.warn(`묶음 수 붙임: "${seoData.productName}" > "${bundle.name}"`);
      seoData.productName = bundle.name;
    } else if (bundle.skipped) {
      console.warn(`묶음 수 «${bundle.skipped}» 를 붙이면 상품명 상한을 넘어 안 붙임: "${seoData.productName}"`);
    }
  },

  /** seoWarning 을 단다 — 이미 있으면(어린이 낱말 + 개수) 한 경고로 합친다 */
  /**
   * 한자가 상품명·옵션명·키워드에 남으면 견적서 최종 검증에서 막힌다(PR261016-D729B4 «창문 紗窗»).
   * 검증 AI 결과도 믿지 않고 한 번 더 본다: 남았으면 검증 AI 1회 재시도 → 그래도 남으면 사전 치환 후 한자를 뺀다(통과시키지 않음).
   */
  async ensureNoHan(product, seoData) {
    if (!seoData) return seoData;
    const leftover = (d) => [d.productName, ...((d.options || []).map(o => o && o.korName)), ...(d.keywords || [])].some(hasHan);
    if (!leftover(seoData)) return seoData;
    console.warn('검증 뒤에도 한자가 남아 재검증:', seoData.productName);
    let data = seoData;
    try {
      data = await this.validateAndTranslateWithAI(seoData, product && product.options);
    } catch (e) {
      console.error('한자 재검증 실패:', e);
    }
    if (!leftover(data)) return data;
    const removed = [];
    const strip = (t) => {
      const r = stripHan(this.removeChinese(String(t || '')));
      removed.push(...r.removed);
      return r.text;
    };
    data = { ...data, productName: strip(data.productName) };
    data.options = (data.options || []).map((o, i) => (o && hasHan(o.korName) ? { ...o, korName: strip(o.korName) || `옵션${i + 1}` } : o));
    data.keywords = (data.keywords || []).map(strip).filter(Boolean);
    if (removed.length) {
      this.addSeoWarning(product, data, 'han', [...new Set(removed)],
        `번역되지 않은 한자 «${[...new Set(removed)].join('·')}»를 상품명·옵션명에서 뺐습니다. 뜻에 맞는 한국어(예: 紗窗=방충망)로 고쳐 주세요.`);
    }
    return data;
  },

  addSeoWarning(product, seoData, type, removedWords, message) {
    const prev = seoData.seoWarning;
    seoData.seoWarning = {
      type: prev ? `${prev.type},${type}` : type,
      removedWords: [...((prev && prev.removedWords) || []), ...removedWords],
      message: prev ? `${prev.message} / ${message}` : message,
      at: Date.now()
    };
    store.commit('SET_SEO_WORD_WARNING', { registrationId: product.registrationId, message: seoData.seoWarning.message, type: seoData.seoWarning.type });
  },

  /**
   * SEO 옵션명(korName)을 그 옵션 원문과 낱말 표로 대조해 바로잡는다(seoSpecGuard fixOptionTerms · PR261021-3D32BB).
   * 바꾸면 seoWarning 에 옛 이름 → 새 이름을 남긴다. SEO 설정 화면 손 저장(productFeature 문자열)은 사람이 고친 것이라 손대지 않는다.
   */
  applyOptionTermGuard(product, seoData) {
    if (!seoData || !Array.isArray(seoData.options) || typeof seoData.productFeature === 'string') return;
    const edits = product && product.quotationUserEdits;
    // AI 가 다시 쓴 옵션명보다 사람이 SEO 화면에 저장한 옵션명이 먼저 — 아래 정리·사실 검사 전에 되돌린다(saram 판 #737 ②)
    const restored = restoreUserKorNames(seoData.options, edits);
    restored.back.forEach(x => console.warn(`⚠️ 옵션 ${x.optionNumber} 옵션명을 사람이 저장한 «${x.to}» 로 되돌림(AI «${x.from}»)`));
    seoData.options = restored.options;
    const r = fixOptionTerms(seoData.options, product && product.options);
    // SEO 화면에서 사람이 저장한 옵션명 그대로인 것은 정리가 안 바꾼다(보고 3320E1 · quotationUserEdits)
    const k = keepUserKorNames(seoData.options, r.options, edits);
    k.kept.forEach(x => console.warn(`⚠️ 옵션 ${x.optionNumber} 옵션명은 사람이 저장한 «${x.from}» 이라 «${x.to}» 로 안 바꿈`));
    const changed = r.changed.filter(c => !k.kept.some(x => x.optionNumber === String(c.optionNumber)));
    if (changed.length) {
      seoData.options = k.options;
      this.addSeoWarning(product, seoData, 'optionTerms', changed.map(c => c.from),
        `옵션명을 원문과 맞춤: ${changed.map(c => `${c.from} → ${c.to}`).join(', ')}`);
    }
    // 옵션명도 상품명과 같은 사실 검사 — 근거 없는 소재·인증 주장·특징 금지 표현(D176A0 «밍크기모» 옵션 9개)
    // 사람이 저장한 옵션명은 바꾸지 않고 경고만 남긴다(saram 판 #737 ① · 사람이 보고 고친다)
    const human = o => o && isUserKorName(edits, o.optionNumber, o.korName);
    const userKept = [];
    const before = seoData.options;
    const c = dropUnsupportedOptionClaims(before, product);
    const isHumanNo = n => human(before.find(o => o && String(o.optionNumber) === String(n)));
    c.empty.concat(c.changed).filter(x => isHumanNo(x.optionNumber)).forEach(x => userKept.push({ from: x.from, removed: x.removed || [] }));
    const cEmpty = c.empty.filter(x => !isHumanNo(x.optionNumber));
    const cChanged = c.changed.filter(x => !isHumanNo(x.optionNumber));
    if (cEmpty.length) throw this.factEmptyError(cEmpty.map(x => `옵션 ${x.optionNumber} «${x.from}»`).join(', '), cEmpty.flatMap(x => x.removed));
    if (cChanged.length) {
      seoData.options = keepUserKorNames(before, c.options, edits).options;
      this.addSeoWarning(product, seoData, 'optionClaim', cChanged.map(x => x.from),
        `옵션명의 근거 없는 소재·인증 주장을 뺐습니다: ${cChanged.map(x => `${x.from} → ${x.to}`).join(', ')}`);
    }
    // 옵션명 원산지 한국 주장 — 상품명·태그와 같은 검사(applyOriginGuard 와 같은 근거)
    const src = { attributes: product.attributes, productFeature: product.productFeature || '', originalTitle: product.productName, purchaseLink: product['구매링크'] };
    const origin = [];
    const empty = [];
    seoData.options = seoData.options.map(o => {
      if (!o || typeof o.korName !== 'string') return o;
      const r = dropOriginClaims(o.korName, src, { allowEmpty: true });
      if (!r.removed.length) return o;
      if (human(o)) { userKept.push({ from: o.korName, removed: r.removed }); return o; }
      if (!r.name) { empty.push({ o, removed: r.removed }); return o; }
      origin.push({ from: o.korName, to: r.name, removed: r.removed });
      return { ...o, korName: r.name };
    });
    if (empty.length) throw this.factEmptyError(empty.map(x => `옵션 ${x.o.optionNumber} «${x.o.korName}»`).join(', '), empty.flatMap(x => x.removed));
    if (origin.length) {
      this.addSeoWarning(product, seoData, 'originClaim', origin.flatMap(x => x.removed),
        `1688(중국 공급처) 상품이라 옵션명의 원산지 한국 주장을 뺐습니다: ${origin.map(x => `${x.from} → ${x.to}`).join(', ')}`);
    }
    if (userKept.length) {
      this.addSeoWarning(product, seoData, 'userKept', userKept.flatMap(x => x.removed),
        `사람이 저장한 옵션명이라 그대로 뒀습니다 — 근거 없는 주장이 있으니 확인해 주세요: ${userKept.map(x => `«${x.from}»(${x.removed.join('·')})`).join(', ')}`);
    }
  },

  /**
   * 사실 검사로 이름이 다 빠졌을 때의 에러 — 원래 거짓 이름을 되살리지 않고 SEO 를 실패시킨다(seoStatus error → 전송 문이 막음 · D176A0 후속).
   * process 의 저장 catch 가 이 code 만 다시 던진다.
   */
  factEmptyError(where, removed) {
    const e = new Error(`${where}은(는) 근거 없는 소재·인증·원산지 주장뿐이라 이름이 비었습니다(뺀 말: ${[...new Set(removed.map(w => String(w).trim()))].join('·')}). 1688 원문·상품 특징에 실제 소재를 적고 SEO 를 다시 돌리세요.`);
    e.code = 'SEO_FACT_EMPTY';
    return e;
  },

  /**
   * SEO 작업 후 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} seoData - SEO 작업 결과 데이터
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, seoData) {
    try {
      if (!product || !product._id) {
        throw new Error('유효하지 않은 상품 정보입니다');
      }

      // 옵션명을 그 옵션 원문과 대조(낱말 표 · PR261021-3D32BB) — 묶음 표기·저장 앞에
      this.applyOptionTermGuard(product, seoData);
      // 묶음 옵션의 "(N개묶음)" 표기를 SEO 결과에 되살린다 (아래 updateFields 전체가 이걸 쓴다)
      const seoOptions = this.applyBundleSuffixToSeoOptions(product, seoData.options);
      // 검색태그로 나가는 키워드는 쿠팡 허용 문자로 정리한다 (아래 저장·검색태그 모두 이걸 쓴다)
      const seoKeywords = this.cleanSearchTagKeywords(seoData.keywords);

      // 점 표기법을 사용하여 업데이트할 필드 지정
      const updateFields = {
        'seoStatus': 'completed',
        'productNameSeo': seoData.productName || '',
        'keywordsSeo': seoKeywords,
        'seo': {
          productName: seoData.productName || '',
          keywords: seoKeywords,
          metaDescription: seoData.metaDescription || '',
          features: seoData.features || [],
          score: seoData.score || 0,
          scoreBreakdown: seoData.scoreBreakdown || [],
          options: seoOptions
        },
        // 어린이용 낱말을 뺐을 때만 값이 있다 — 다시 만들거나 손으로 저장하면 지워진다(runWork·startQueue 응답이 읽는다)
        'seoWarning': seoData.seoWarning || null,
        'updatedAt': Date.now()
      };
      // SEO 설정 화면 저장만 상품 특징을 보낸다(빈 문자열 = 사용자가 비운 것, 그대로 저장). AI 자동 생성 결과엔 이 키가 없어 손대지 않는다.
      if (typeof seoData.productFeature === 'string') {
        updateFields.productFeature = seoData.productFeature;
        // 손 저장 옵션명 = 사람 값 — 뒤 SEO 정리가 그 글을 안 바꾸게 적는다(보고 3320E1).
        // 묶음 표기(N개묶음)를 붙이기 전 이름으로 적는다 — 정리가 대조하는 것도 붙이기 전이다
        updateFields.quotationUserEdits = recordKorNames(product, seoData.options);
      }

      // 옵션 정보 업데이트
      if (seoOptions.length && product.options) {
        seoOptions.forEach(seoOption => {
          if (seoOption.optionNumber) {
            // 해당 옵션 찾기
            const optionIndex = product.options.findIndex(opt =>
              opt.optionNumber === seoOption.optionNumber);

            if (optionIndex !== -1) {
              // 점 표기법으로 특정 옵션의 특정 필드만 업데이트
              updateFields[`options.${optionIndex}.korName`] = seoOption.korName;
              updateFields[`options.${optionIndex}.seoStatus`] = 'completed';
            }
          }
        });
      }

      // coupangDoc 업데이트 - 점 표기법 사용
      if (product.coupangDoc) {
        // 1. coupangDoc.productName 업데이트 (옵션명 생략)
        const oldCoupangDocProductName = product.coupangDoc.productName;
        const newCoupangDocProductName = seoData.productName || product.productNameSeo || product.productNameTrans || '';
        updateFields['coupangDoc.productName'] = newCoupangDocProductName;
        console.log(`🔄 SEO coupangDoc.productName업데이트: "${oldCoupangDocProductName}" > "${newCoupangDocProductName}"`);

        // 2. 각 옵션별 jsonDocument 업데이트
        if (product.coupangDoc.jsonDocument) {
          product.coupangDoc.jsonDocument.forEach((docItem, index) => {
            // 해당 인덱스의 옵션 찾기
            const option = product.options && product.options.length > index ? product.options[index] : null;
            const optionKorName = option ? (option.korName || '') : '';

            // startPage 업데이트 - "SEO된 productName, 옵션명" 형태로
            if (docItem.startPage) {
              // 해당 옵션의 SEO된 korName 찾기
              let seoOptionKorName = optionKorName; // 기본값으로 기존 옵션명 사용

              // seoOptions에서 해당 옵션의 korName 찾기
              if (seoOptions.length) {
                const targetOptionNumber = option ? option.optionNumber : null;

                const matchedOption = seoOptions.find(opt => {
                  // 옵션 번호 정규화 (Option 접두사 제거)
                  const normalizedSeoOptionNumber = this.normalizeOptionNumber(opt.optionNumber);
                  const normalizedTargetOptionNumber = this.normalizeOptionNumber(targetOptionNumber);

                  return normalizedSeoOptionNumber === normalizedTargetOptionNumber;
                });

                if (matchedOption && matchedOption.korName) {
                  seoOptionKorName = matchedOption.korName;
                  console.log(`옵션 ${index}의 SEO된 korName 찾음:`, seoOptionKorName);
                  console.log(`매칭 정보: 원본(${option.optionNumber}) ↔ SEO(${matchedOption.optionNumber})`);
                } else {
                  console.warn(`옵션 ${index}의 SEO된 korName을 찾을 수 없습니다.`);
                  console.log(`대상 옵션번호: ${targetOptionNumber}`);
                  console.log(`SEO 옵션들:`, seoOptions.map(opt => opt.optionNumber));
                }
              }

              // 옵션명이 있으면 "상품명, 옵션명" 형태로, 없으면 상품명만
              const fullProductName = seoOptionKorName
                ? `${seoData.productName}`
                : seoData.productName;

              updateFields[`coupangDoc.jsonDocument.${index}.startPage.productName`] = fullProductName;
              console.log(`옵션 ${index} startPage.productName 업데이트:`, fullProductName);
            }

            // productPage 업데이트
            if (docItem.productPage) {
              // 검색 태그 업데이트 (150자 제한)
              let searchTags = '';
              if (seoKeywords.length > 0) {
                searchTags = seoKeywords.join(', ');

                // 150자 제한 처리
                if (searchTags.length > 150) {
                  console.log(`검색 태그 길이(${searchTags.length})가 150자를 초과합니다. 태그를 줄입니다.`);

                  // 태그를 뒤에서부터 제거하면서 150자 이하로 맞추기
                  const keywords = [...seoKeywords];
                  for (let i = 0; i < keywords.length; i++) {
                    const potentialKeywords = keywords.slice(0, keywords.length - i);
                    const potentialString = potentialKeywords.join(', ');

                    if (potentialString.length <= 150) {
                      searchTags = potentialString;
                      console.log(`원래 태그 수: ${keywords.length}, 줄인 태그 수: ${potentialKeywords.length}`);
                      break;
                    }
                  }
                }
              } else {
                searchTags = docItem.productPage.searchTags || '';
              }

              updateFields[`coupangDoc.jsonDocument.${index}.productPage.searchTags`] = searchTags;
              console.log(`옵션 ${index} productPage.searchTags 업데이트:`, searchTags.substring(0, 50) + '...');
            }
          });
        }
      }

      // 서버에 부분 업데이트 요청
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      return true;
    } catch (error) {
      console.error('SEO 정보로 상품 업데이트 중 오류:', error);
      throw error;
    }
  },
  /**
   * SEO 결과에 묶음 표기 "(N개묶음)" 를 되살린다.
   *
   * SEO 프롬프트에는 옵션의 option1/option2(원문)만 나간다. 묶음 옵션은 원본을 깊은 복사한
   * 것이라 원문이 같아서 AI 가 원본과 똑같은 이름을 돌려준다 — 그대로 저장하면 2개들이인데
   * 1개짜리 이름이 되어 손님이 잘못 산다.
   *
   * 묶음 옵션명 = (원본의 SEO 이름) + 접미사. 원본이 SEO 로 좋아지면 묶음도 같이 따라간다.
   * 접미사 조립 규칙은 bundleOptionService.withBundleSuffix 하나만 쓴다.
   *
   * @param {Object} product - 상품 객체
   * @param {Array} rawSeoOptions - SEO 결과의 options 배열
   * @returns {Array} 묶음 표기가 반영된 options 배열 (묶음이 없으면 원본 그대로)
   */
  applyBundleSuffixToSeoOptions(product, rawSeoOptions) {
    const seoOptions = Array.isArray(rawSeoOptions) ? rawSeoOptions : [];
    const productOptions = (product && Array.isArray(product.options)) ? product.options : [];

    // 묶음 옵션 추리기 (판별 규칙은 bundleOptionService 가 갖는다)
    const bundles = [];
    productOptions.forEach(option => {
      const info = bundleOptionService.bundleLabelInfo(option);
      if (info) bundles.push({ option: option, info: info });
    });

    // 묶음 옵션이 없으면 아무것도 바꾸지 않는다 (일반 상품은 지금 동작 그대로)
    if (!bundles.length) return seoOptions;

    const seoKorNameByNumber = {};
    seoOptions.forEach(seoOption => {
      if (seoOption && seoOption.optionNumber) {
        seoKorNameByNumber[seoOption.optionNumber] = seoOption.korName;
      }
    });

    // 기준 이름 = 원본의 SEO 이름 > 묶음 자신의 SEO 이름 > 지금 저장된 이름
    const resolveBundleKorName = (bundle) => {
      const baseName = seoKorNameByNumber[bundle.info.originalOptionNumber]
        || seoKorNameByNumber[bundle.option.optionNumber]
        || bundle.option.korName;

      if (!baseName) return null;
      return bundleOptionService.withBundleSuffix(baseName, bundle.info.bundleQuantity);
    };

    const bundleByNumber = {};
    bundles.forEach(bundle => { bundleByNumber[bundle.option.optionNumber] = bundle; });

    // 사람이 SEO 화면에 저장한 묶음 옵션명은 다시 만들지 않는다(총괄 대조 판 #737 — 원본 이름 + 접미사가 덮었다)
    const edits = product && product.quotationUserEdits;
    const result = seoOptions.map(seoOption => {
      const bundle = seoOption && bundleByNumber[seoOption.optionNumber];
      if (!bundle) return seoOption;
      const human = userKorNameOf(edits, seoOption.optionNumber);
      if (human) return { ...seoOption, korName: human };

      const korName = resolveBundleKorName(bundle);
      if (!korName) return seoOption;

      console.log(`🔗 묶음 옵션명 복원: ${seoOption.optionNumber} "${seoOption.korName}" > "${korName}"`);
      return { ...seoOption, korName: korName };
    });

    // SEO 결과에서 아예 빠진 묶음 옵션은 채워 넣는다 (안 넣으면 원본만 새 이름이 되어 짝이 어긋난다)
    bundles.forEach(bundle => {
      if (Object.prototype.hasOwnProperty.call(seoKorNameByNumber, bundle.option.optionNumber)) return;

      const korName = userKorNameOf(edits, bundle.option.optionNumber) || resolveBundleKorName(bundle);
      if (!korName) return;

      console.log(`🔗 SEO 결과에 없는 묶음 옵션 보충: ${bundle.option.optionNumber} > "${korName}"`);
      result.push({ optionNumber: bundle.option.optionNumber, korName: korName });
    });

    return result;
  },
  /**
 * SEO 설정 처리 메인 함수
 * @param {Object} product - 상품 객체
 * @param {Object} data - 요청 데이터
 * @returns {Promise<Object>} 처리 결과
 */
  async processSeo(product, data = {}) {
    try {
      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: 'SEO 설정 작업 중...'
      });

      let seoResult = null;

      // SEO 설정 모드에 따라 처리
      if (data.mode === 'manual' && data.manualData) {
        // 수동 SEO 설정
        seoResult = this.processManualSeo(data.manualData);
      } else {
        // AI 자동 SEO 설정 (재시도 로직 포함)
        seoResult = await this.generateSeoWithRetry(product);
      }

      // 옵션 정보 추가
      if (product.options && product.options.length > 0) {
        seoResult.options = product.options.map(option => ({
          id: option.id,
          optionNumber: option.optionNumber,
          korName: option.korName || ''
        }));
      }

      // 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
      try {
        await this.updateProductWithCoupangDocPartial(product, seoResult);
        console.log('SEO 정보로 상품 업데이트 완료');
      } catch (updateError) {
        console.error('SEO 정보로 상품 업데이트 실패:', updateError);
        if (updateError && updateError.code === 'SEO_FACT_EMPTY') throw updateError;   // 사실 검사로 이름이 빔 = SEO 실패(전송 막음)
        // 업데이트 실패해도 SEO 결과는 반환
      }

      return {
        status: 'completed',
        type: 'seoStatus',
        seoInfo: seoResult
      };
    } catch (error) {
      console.error('SEO 설정 처리 중 오류:', error);
      return {
        status: 'error',
        type: 'seoStatus',
        error: error.message
      };
    } finally {
      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false
      });
    }
  },

  /**
 * 수동 SEO 설정 처리
 * @param {Object} manualData - 수동 SEO 설정 데이터
 * @returns {Object} 처리 결과
 */
  processManualSeo(manualData) {
    // 유효성 검사
    if (!manualData.productName) {
      throw new Error('상품명은 필수 항목입니다.');
    }

    // 키워드 처리 (문자열에서 배열로 변환)
    let keywords = Array.isArray(manualData.keywords)
      ? manualData.keywords
      : (manualData.keywordsText || '').split(',').map(k => k.trim()).filter(k => k);

    // 검색 태그 길이 제한 (150자)
    if (keywords.length > 0) {
      const joinedKeywords = keywords.join(', ');

      if (joinedKeywords.length > 150) {
        console.log(`검색 태그 길이(${joinedKeywords.length})가 150자를 초과합니다. 태그를 줄입니다.`);

        // 태그를 뒤에서부터 제거하면서 150자 이하로 맞추기
        let trimmedKeywords = [];
        for (let i = 0; i < keywords.length; i++) {
          const potentialKeywords = keywords.slice(0, keywords.length - i);
          const potentialString = potentialKeywords.join(', ');

          if (potentialString.length <= 150) {
            trimmedKeywords = potentialKeywords;
            console.log(`원래 태그 수: ${keywords.length}, 줄인 태그 수: ${potentialKeywords.length}`);
            break;
          }
        }

        keywords = trimmedKeywords;
        console.log(`최종 검색 태그 길이: ${keywords.join(', ').length}`);
      }
    }

    // 특징 처리 (빈 항목 제거)
    const features = Array.isArray(manualData.features)
      ? manualData.features.filter(f => f.trim().length > 0)
      : [];

    // 옵션 정보 처리
    const options = Array.isArray(manualData.options)
      ? manualData.options
      : [];

    // SEO 점수 계산
    const scoreData = this.calculateSeoScore(manualData.productName, keywords, manualData.metaDescription, features);

    // 최종 SEO 데이터 구성
    return {
      productName: manualData.productName,
      keywords: keywords,
      metaDescription: manualData.metaDescription || '',
      features: features,
      options: options,
      score: scoreData.totalScore,
      scoreBreakdown: scoreData.breakdown
    };
  },
  /**
   * AI를 활용한 SEO 설정 처리
   * 옵션이 20개 이상인 경우 배치 처리로 분할
   * @param {Object} product - 상품 객체
   * @param {Object} options - 옵션
   * @returns {Promise<Object>} SEO 데이터
   */
  async processAiSeo(product, options) {
    try {
      const BATCH_SIZE = 20; // 배치당 옵션 수 (프롬프트 길이 제한을 위해 20개로 설정)
      const totalOptions = product.options?.length || 0;
      const needsBatchProcessing = totalOptions > BATCH_SIZE;

      console.log(`📊 SEO 처리 시작 - 옵션 수: ${totalOptions}, 배치 처리 필요: ${needsBatchProcessing}`);

      // 로딩 메시지 업데이트
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: needsBatchProcessing
          ? `AI를 활용한 SEO 분석 준비 중... (${totalOptions}개 옵션 배치 처리)`
          : 'AI를 활용한 SEO 분석 준비 중...'
      });

      // 연관 키워드 수집 (아직 없는 경우)
      let relatedKeywords = options.relatedKeywords || [];
      if (!relatedKeywords.length && options.targetKeyword) {
        try {
          store.dispatch('setProcessing', {
            isProcessing: true,
            message: '쿠팡에서 연관 키워드 수집 중...'
          });

          // workflowService를 통해 연관 키워드 수집 (재시도 로직 포함)
          relatedKeywords = await workflowService.collectCoupangKeywords(
            options.targetKeyword,
            { cycle: 1, relatedKeywords: true, autoComplete: true }
          );

          store.dispatch('setProcessing', {
            isProcessing: true,
            message: `${relatedKeywords.length}개 연관 키워드 수집 완료`
          });
        } catch (e) {
          console.warn('연관 키워드 수집 실패:', e);
          // 실패해도 계속 진행 (필수 기능은 아니므로)
        }
      }

      // 배치 처리가 필요한 경우
      if (needsBatchProcessing) {
        return await this.processAiSeoBatched(product, options, relatedKeywords, BATCH_SIZE);
      }

      // 일반 처리 (옵션이 적은 경우)
      return await this.processAiSeoSingle(product, options, relatedKeywords);
    } catch (error) {
      console.error('AI SEO 설정 처리 중 오류:', error);
      throw error;
    }
  },

  /**
   * 옵션이 많을 때 배치 처리로 SEO 생성
   * @param {Object} product - 상품 객체
   * @param {Object} options - 옵션
   * @param {Array} relatedKeywords - 연관 키워드
   * @param {number} batchSize - 배치 크기
   * @returns {Promise<Object>} SEO 데이터
   */
  async processAiSeoBatched(product, options, relatedKeywords, batchSize) {
    const totalOptions = product.options?.length || 0;
    const totalBatches = Math.ceil(totalOptions / batchSize);
    console.log(`🔄 배치 SEO 처리 시작 - 총 ${totalBatches}개 배치`);

    // 1. 첫 번째 배치: 상품명/키워드 + 첫 번째 배치 옵션
    store.dispatch('setProcessing', {
      isProcessing: true,
      message: `SEO 생성 중... (배치 1/${totalBatches})`
    });

    // 첫 번째 배치용 임시 상품 객체 생성 (옵션 제한)
    const firstBatchOptions = product.options.slice(0, batchSize);
    const firstBatchProduct = {
      ...product,
      options: firstBatchOptions
    };

    const promptOptions = {
      ...options,
      relatedKeywords,
      // 상품명은 첫 배치 20개가 아니라 전체 옵션을 보고 짓게 한다(PR261016-FCD580)
      allOptionsSummary: summarizeOptionValues(product.options)
    };
    const firstPrompt = this.promptForSeo(firstBatchProduct, promptOptions);

    let seoData;
    try {
      const result = await this.requestExtension({
        type: 'SEO',
        productId: product.registrationId,
        prompt: firstPrompt
      }, { timeout: 90000, maxRetries: 2 }); // 타임아웃 90초

      seoData = this.extractSeoDataFromResult(result);

      if (!seoData || !seoData.productName) {
        throw new Error('첫 번째 배치 SEO 데이터 필수 필드 누락');
      }

      console.log(`✅ 배치 1/${totalBatches} 완료 - 옵션 ${seoData.options?.length || 0}개 번역됨`);
    } catch (error) {
      console.error('첫 번째 배치 처리 실패:', error);
      throw error;
    }

    // 2. 나머지 배치: 옵션 번역만 요청
    const allTranslatedOptions = [...(seoData.options || [])];

    // 첫 번째 배치에서 번역된 옵션 예시 3개 추출 (일관성 유지용)
    const exampleOptions = [];
    if (seoData.options && seoData.options.length > 0) {
      const firstBatchOptions = product.options.slice(0, batchSize);
      for (let i = 0; i < Math.min(3, seoData.options.length); i++) {
        const translatedOpt = seoData.options[i];
        const originalOpt = firstBatchOptions.find(o => o.optionNumber === translatedOpt.optionNumber);
        if (originalOpt && translatedOpt.korName) {
          exampleOptions.push({
            original: originalOpt,
            translated: translatedOpt
          });
        }
      }
      console.log(`📝 번역 일관성을 위한 예시 ${exampleOptions.length}개 추출 완료`);
    }

    for (let batchIndex = 1; batchIndex < totalBatches; batchIndex++) {
      const startIdx = batchIndex * batchSize;
      const endIdx = Math.min(startIdx + batchSize, totalOptions);
      const batchOptions = product.options.slice(startIdx, endIdx);

      store.dispatch('setProcessing', {
        isProcessing: true,
        message: `SEO 생성 중... (배치 ${batchIndex + 1}/${totalBatches}, 옵션 ${startIdx + 1}-${endIdx})`
      });

      try {
        // 예시 옵션을 포함한 프롬프트 생성
        const batchPrompt = this.promptForOptionTranslation(batchOptions, exampleOptions);
        const batchResult = await this.requestExtension({
          type: 'SEO',
          productId: product.registrationId,
          prompt: batchPrompt
        }, { timeout: 90000, maxRetries: 2 }); // 타임아웃 90초

        const batchData = this.extractSeoDataFromResult(batchResult);
        if (batchData && batchData.options && Array.isArray(batchData.options)) {
          allTranslatedOptions.push(...batchData.options);
          console.log(`✅ 배치 ${batchIndex + 1}/${totalBatches} 완료 - 옵션 ${batchData.options.length}개 번역됨`);
        } else {
          console.warn(`⚠️ 배치 ${batchIndex + 1} 결과에서 옵션 데이터 없음`);
        }
      } catch (batchError) {
        console.error(`❌ 배치 ${batchIndex + 1}/${totalBatches} 실패:`, batchError);
        // 일부 배치 실패 시 계속 진행 (기존 번역 결과 유지)
      }

      // 배치 간 짧은 대기 (Rate Limit 방지)
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 3. 결과 병합
    seoData.options = allTranslatedOptions;
    console.log(`📊 배치 SEO 완료 - 총 ${allTranslatedOptions.length}/${totalOptions}개 옵션 번역됨`);

    // 🤖 AI를 통한 중국어 검증 및 재번역
    console.log('🤖 AI를 통한 중국어 검증 및 재번역 시작');
    seoData = await this.validateAndTranslateWithAI(seoData, product && product.options);
    seoData = await this.ensureNoHan(product, seoData);
    // 원문 제목의 규격이 남긴 옵션과 다르면 상품명에서 뺀다(전체 옵션 기준 — 첫 배치만 보지 않는다)
    seoData.productName = dropSpecsNotInOptions(seoData.productName, product.options);
    seoData.productName = dropPartialOptionParens(seoData.productName, product.options, seoData.options);
    this.applyKidsWordGuard(product, seoData, (options && options.productFeature) || product.productFeature || '', (options && options.targetKeyword) || product.targetKeyword || '');
    this.applyCountGuard(product, seoData, (options && options.productFeature) || product.productFeature || '', (options && options.targetKeyword) || product.targetKeyword || '');
    this.applyOriginGuard(product, seoData, (options && options.productFeature) || product.productFeature || '');

    // SEO 점수 계산
    if (!seoData.score) {
      const scoreData = this.calculateSeoScore(
        seoData.productName,
        seoData.keywords || [],
        seoData.metaDescription || '',
        seoData.features || []
      );
      seoData.score = scoreData.totalScore;
      seoData.scoreBreakdown = scoreData.breakdown;
    }

    return seoData;
  },

  /**
   * 옵션 번역 전용 프롬프트 생성
   * @param {Array} options - 번역할 옵션 배열
   * @param {Array} exampleOptions - 일관성 유지를 위한 예시 옵션 (선택)
   * @returns {string} 프롬프트
   */
  promptForOptionTranslation(options, exampleOptions = []) {
    // 예시 옵션 섹션 생성 (있는 경우)
    let examplesSection = '';
    if (exampleOptions && exampleOptions.length > 0) {
      examplesSection = '\nTRANSLATION EXAMPLES (follow this style for consistency):\n';
      exampleOptions.forEach((ex, idx) => {
        const orig = ex.original;
        const trans = ex.translated;
        const option1Info = orig.option1 ? `${orig.option1.name || ''} (${orig.option1.nameTrans || ''})` : '';
        const option2Info = orig.option2 ? `${orig.option2.name || ''} (${orig.option2.nameTrans || ''})` : '';
        examplesSection += `Example ${idx + 1}:\n`;
        examplesSection += `  Original: ${option1Info}${option2Info ? ' / ' + option2Info : ''}\n`;
        examplesSection += `  Translated: "${trans.korName}"\n`;
      });
      examplesSection += '\nFollow the same translation style and format as the examples above.\n';
    }

    let optionsInfo = '';
    options.forEach((option, index) => {
      const option1Info = option.option1 ? `${option.option1.name || ''} (${option.option1.nameTrans || ''})` : '';
      const option2Info = option.option2 ? `${option.option2.name || ''} (${option.option2.nameTrans || ''})` : '';
      optionsInfo += `${index + 1}. 옵션번호: ${option.optionNumber}\n`;
      if (option1Info) optionsInfo += `   옵션1: ${option1Info}\n`;
      if (option2Info) optionsInfo += `   옵션2: ${option2Info}\n`;
    });

    return `You are a Korean translation specialist. Translate the following product options to Korean.
${examplesSection}
OPTIONS TO TRANSLATE:
${optionsInfo}

RULES:
- ALL option names MUST be in Korean only - ABSOLUTELY NO Chinese characters
- Translate color names: black→블랙, white→화이트, red→레드, blue→블루, etc.
- Convert sizes to Korean: 大→대형, 中→중형, 小→소형
- If multiple values exist (e.g., "9cm/12cm"), choose ONE representative value
- Keep option numbers exactly as provided
- IMPORTANT: Follow the same translation style as the examples provided above

Respond in this exact JSON format:
{
  "options": [
    {
      "optionNumber": "keep original option number exactly",
      "korName": "KOREAN ONLY translated option name"
    }
  ]
}`;
  },

  /**
   * SEO 결과에서 데이터 추출 (공통 함수)
   * @param {any} result - API 결과
   * @returns {Object|null} 추출된 SEO 데이터
   */
  extractSeoDataFromResult(result) {
    const extractSeoData = (data) => {
      if (data && data.batchResults && Array.isArray(data.batchResults) && data.batchResults.length > 0) {
        const firstBatch = data.batchResults[0];
        if (firstBatch.result && typeof firstBatch.result === 'object') {
          return firstBatch.result;
        }
        if (firstBatch.processedText) {
          try {
            let jsonText = firstBatch.processedText;
            jsonText = jsonText.replace(/```json\n|\n```/g, '');
            jsonText = jsonText.replace(/```\n|\n```/g, '');
            jsonText = jsonText.trim();
            return JSON.parse(jsonText);
          } catch (parseError) {
            console.error('processedText 파싱 오류:', parseError);
          }
        }
        return firstBatch;
      }
      return data;
    };

    if (typeof result === 'string') {
      try {
        const parsedData = JSON.parse(result);
        return extractSeoData(parsedData);
      } catch (parseError) {
        const jsonMatch = result.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            return extractSeoData(JSON.parse(jsonMatch[0]));
          } catch (e) {
            console.error('JSON 추출 실패:', e);
          }
        }
      }
    } else if (typeof result === 'object') {
      return extractSeoData(result);
    }
    return null;
  },

  /**
   * 일반 SEO 처리 (옵션이 적은 경우)
   * @param {Object} product - 상품 객체
   * @param {Object} options - 옵션
   * @param {Array} relatedKeywords - 연관 키워드
   * @returns {Promise<Object>} SEO 데이터
   */
  async processAiSeoSingle(product, options, relatedKeywords) {
    // 로딩 메시지 업데이트
    store.dispatch('setProcessing', {
      isProcessing: true,
      message: 'AI로 SEO 정보 생성 요청 중...'
    });

    // 프롬프트에 연관 키워드 포함
    const promptOptions = {
      ...options,
      relatedKeywords
    };
    const prompt = this.promptForSeo(product, promptOptions);

    try {
      // 확장 프로그램에 요청 보내고 응답 기다리기
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: 'SEO 정보 생성 중... (최대 30초 소요)'
      });

      // 개선된 requestExtension 메서드 사용
      const result = await this.requestExtension({
        type: 'SEO',
        productId: product.registrationId,
        prompt
      }, { timeout: 90000, maxRetries: 2 }); // 90초 타임아웃, 최대 2회 재시도

      console.log('SEO 응답 수신:', result);

      // 응답 결과 파싱
      let seoData = this.extractSeoDataFromResult(result);

      // 필수 필드 확인
      if (!seoData || !seoData.productName) {
        console.warn('SEO 데이터에 필수 필드가 없습니다.');
        throw new Error('SEO 데이터 필수 필드 누락');
      }

      // 🤖 AI를 통한 중국어 검증 및 재번역 (더 정확한 방법)
      console.log('🤖 AI를 통한 중국어 검증 및 재번역 시작');
      seoData = await this.validateAndTranslateWithAI(seoData, product && product.options);
      seoData = await this.ensureNoHan(product, seoData);
      // 원문 제목의 규격이 남긴 옵션과 다르면 상품명에서 뺀다
      seoData.productName = dropSpecsNotInOptions(seoData.productName, product.options);
      seoData.productName = dropPartialOptionParens(seoData.productName, product.options, seoData.options);
      this.applyKidsWordGuard(product, seoData, (options && options.productFeature) || product.productFeature || '', (options && options.targetKeyword) || product.targetKeyword || '');
      this.applyCountGuard(product, seoData, (options && options.productFeature) || product.productFeature || '', (options && options.targetKeyword) || product.targetKeyword || '');
      this.applyOriginGuard(product, seoData, (options && options.productFeature) || product.productFeature || '');

      // SEO 점수 계산 (없는 경우)
      if (!seoData.score) {
        const scoreData = this.calculateSeoScore(
          seoData.productName,
          seoData.keywords || [],
          seoData.metaDescription || '',
          seoData.features || []
        );
        seoData.score = scoreData.totalScore;
        seoData.scoreBreakdown = scoreData.breakdown;
      }

      return seoData;
    } catch (extensionError) {
      console.error('확장 프로그램 요청 실패:', extensionError);
      logAIRequestError('seoService', 'generateSeoData', extensionError, {
        fallbackAction: '에러 전파 (재시도 처리)',
        productId: product?.registrationId,
        additional: { via: 'extension' }
      });
      // 에러를 상위로 전파 (generateSeoWithRetry에서 처리)
      throw extensionError;
    }
  },

  /**
   * 기본 옵션명 생성
   * @param {Object} product - 상품 객체
   * @returns {Array} 생성된 옵션 배열
   */
  generateDefaultOptions(product) {
    if (!product.options || product.options.length === 0) return [];

    return product.options.map(option => {
      let korName = '';

      // 옵션 정보에서 색상 키워드 추출
      const optionName = option.option1 ? (option.option1.name || '') : '';
      const optionNameTrans = option.option1 ? (option.option1.nameTrans || '') : '';

      // 색상 키워드 매칭
      const colorMap = {
        'black': '블랙',
        'white': '화이트',
        'red': '레드',
        'blue': '블루',
        'green': '그린',
        'yellow': '옐로우',
        'purple': '퍼플',
        'pink': '핑크',
        'gray': '그레이',
        'brown': '브라운',
        'beige': '베이지',
        'silver': '실버',
        'gold': '골드'
      };

      // 영문 색상 찾기
      let foundColor = '';
      Object.keys(colorMap).forEach(color => {
        if (optionName.toLowerCase().includes(color) ||
          optionNameTrans.toLowerCase().includes(color)) {
          foundColor = colorMap[color];
        }
      });

      // 타겟 키워드
      const targetKeyword = product.targetKeyword || '상품';

      // 한글 옵션명 구성
      if (foundColor) {
        korName = `${foundColor} ${targetKeyword}`;
      } else {
        korName = `${targetKeyword} ${optionNameTrans || '옵션'}`;
      }

      return {
        id: option.id,
        optionNumber: option.optionNumber,
        korName: korName
      };
    });
  },

  /**
   * 확장 프로그램에 요청을 보내고 응답을 기다립니다.
   * @param {Object} requestData - 요청 데이터
   * @param {Object} options - 요청 옵션
   * @param {number} options.timeout - 타임아웃 시간(ms), 기본값 30000ms
   * @param {number} options.maxRetries - 최대 재시도 횟수, 기본값 2
   * @returns {Promise<Object>} 응답 데이터
   */
  async requestExtension(requestData, options = {}) {
    const timeout = options.timeout || 30000; // 기본 타임아웃 30초
    const maxRetries = options.maxRetries || 2; // 기본 최대 재시도 횟수 2회

    try {
      console.log(`SEO 요청 시작 (workType: ${requestData.type})`);

      // workflowService의 requestExtensionWithPromise 함수 사용
      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: requestData.type,
          productId: requestData.productId,
          requestArr: requestData.requestArr || (requestData.prompt ? [{ prompt: requestData.prompt }] : [])
        },
        { timeout, maxRetries }
      );

      console.log(`SEO 응답 수신 성공`);
      return result;
    } catch (error) {
      console.error(`SEO 요청 실패:`, error);
      throw error;
    }
  },

  /**
   * Gemini API 오류 처리 메서드 (재시도 로직 포함)
   * @param {Error} error - 발생한 오류
   * @returns {Object} 오류 처리 정보
   */
  handleGeminiApiError(error) {
    let userMessage = '';
    let isGeminiError = false;
    let shouldRetry = false;
    let shouldStopProcess = false;

    const errorMessage = error.message || error.toString();

    if (errorMessage.includes('400') || errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('API key')) {
      userMessage = 'API_KEY_ERROR';
      isGeminiError = true;
      shouldRetry = false;
      shouldStopProcess = true;
    } else if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
      userMessage = 'RATE_LIMIT_ERROR';
      isGeminiError = true;
      shouldRetry = false;
      shouldStopProcess = true;
    } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
      userMessage = '네트워크 연결에 문제가 있습니다. 인터넷 연결을 확인해주세요.';
      isGeminiError = false; // 네트워크 문제는 워크플로우 단계 재시도
      shouldRetry = true;
      shouldStopProcess = false;
    } else if (errorMessage.includes('quota') || errorMessage.includes('exceeded')) {
      userMessage = 'QUOTA_ERROR';
      isGeminiError = true;
      shouldRetry = false;
      shouldStopProcess = true;
    } else if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
      userMessage = 'SERVER_OVERLOAD_ERROR';
      isGeminiError = true;
      shouldRetry = false;
      shouldStopProcess = true;
    } else {
      userMessage = 'AI 처리 중 오류가 발생했습니다. 워크플로우 단계를 재시도합니다.';
      isGeminiError = false; // 일반 오류는 워크플로우 단계 재시도
      shouldRetry = true;
      shouldStopProcess = false;
    }

    // Gemini API 에러인 경우 프로세스 중단 및 해결 방법 안내
    if (shouldStopProcess && typeof window !== 'undefined') {
      this.showGeminiErrorAlert(userMessage);
    }

    return {
      isGeminiError,
      shouldRetry,
      shouldStopProcess,
      userMessage,
      retryDelay: isGeminiError ? 60000 : 5000 // Gemini 오류는 1분, 일반 오류는 5초
    };
  },

  showGeminiErrorAlert(errorType) {
    if (typeof window !== 'undefined') {
      let message = '';

      if (errorType === 'API_KEY_ERROR') {
        message = `⚠️ Gemini API Key 오류

Gemini API Key가 유효하지 않습니다.

기본설정 > Gemini 설정에서 API Key를 재발급해주세요.

1. 기존 API Key 삭제
2. 새로운 API Key 발급
3. 발급받은 Key를 기본설정 > Gemini 설정에 입력

💡 Google AI Studio에서 무료로 발급받을 수 있습니다.`;
      } else if (errorType === 'RATE_LIMIT_ERROR' || errorType === 'QUOTA_ERROR' || errorType === 'SERVER_OVERLOAD_ERROR') {
        message = `⚠️ Gemini API 사용량 한도 초과

Gemini API 무료 티어의 사용량 한도를 초과했습니다.

아래 순서대로 시도해보세요:

1. Gemini 2.0 Flash Lite로 변경 (무료)
   → 기본설정 > Gemini 설정에서 모델 변경

2. Gemini API Key 재발급 (무료)
   → 기존 키 삭제 후 새로 발급

3. Gemini API Key 유료 발급
   → 월 1~2만원 API 비용 예상

💡 무료 티어는 요청 제한이 있어 간헐적으로 문제가 발생할 수 있습니다.`;
      } else {
        message = `⚠️ Gemini API 오류

Gemini API 처리 중 오류가 발생했습니다.

기본설정 > Gemini 설정에서 API Key와 모델을 확인해주세요.`;
      }

      // alert 는 사람이 누를 때까지 화면 JS 전체를 세운다 — 에이전트가 시킨 작업(데몬 명령)과 살아있음 신호까지 멈춘다.
      // 비차단 안내로 바꾼다(App.vue 가 받아 토스트로 띄운다). 작업 자체는 호출부가 던지는 오류로 멈추고 그 사유가 결과로 간다
      window.dispatchEvent(new CustomEvent('couplus:notice', { detail: { title: 'Gemini API 오류', message, variant: 'danger' } }));
    }
  },

  /** 오류 종류 → 결과에 실을 한 줄(에이전트·작업 기록용) */
  geminiErrorText(errorType) {
    if (errorType === 'API_KEY_ERROR') return 'Gemini API Key 가 유효하지 않습니다(기본설정 > Gemini 설정에서 재발급 필요).';
    if (errorType === 'RATE_LIMIT_ERROR' || errorType === 'QUOTA_ERROR' || errorType === 'SERVER_OVERLOAD_ERROR') {
      return 'Gemini API 사용량 한도를 넘었습니다(모델 변경·키 재발급·유료 키 중 하나가 필요).';
    }
    return 'Gemini API 처리 중 오류가 났습니다(기본설정 > Gemini 설정의 키·모델 확인 필요).';
  },

  /** 1688 원 제목의 형태·색·겹 수가 남긴 옵션과 다르면 상품명(·태그)에서 뺀다 — 거름은 mainpc seoSpecGuard 그대로(10-20 optname-1) · applyCountGuard 가 부른다 */
  applyOptionWordGuards(product, seoData) {
    const shape = dropShapeWordsNotInOptions(seoData.productName, product.productName, product.options);
    if (shape.removed.length) {
      console.warn(`옵션과 다른 형태 낱말 제거: "${seoData.productName}" > "${shape.name}"`);
      seoData.productName = shape.name;
      this.addSeoWarning(product, seoData, 'shape', shape.removed,
        `상품명의 «${shape.removed.join('·')}»가 남긴 옵션의 형태와 달라 뺐습니다(1688 원래 제목의 표현). 옵션 이름에도 같은 낱말이 있으면 확인하세요.`);
    }
    const color = dropColorsNotInOptions(seoData.productName, seoData.keywords, product.productName, product.options);
    if (color.removed.length) {
      console.warn(`옵션과 다른 색 낱말 제거: "${seoData.productName}" > "${color.name}"`);
      seoData.productName = color.name;
      if (Array.isArray(seoData.keywords)) seoData.keywords = color.keywords;
      this.addSeoWarning(product, seoData, 'color', color.removed,
        `상품명·검색태그의 «${[...new Set(color.removed.map(w => String(w).trim()))].join('·')}»가 남긴 옵션의 색과 달라 뺐습니다(1688 원래 제목의 색). 옵션 색을 상품명에 넣으려면 직접 적어 저장하세요.`);
    }
    const layer = dropLayerWordsNotInOptions(seoData.productName, seoData.keywords, product.productName, product.options);
    if (layer.removed.length) {
      console.warn(`옵션과 다른 겹 수 낱말 제거: "${seoData.productName}" > "${layer.name}"`);
      seoData.productName = layer.name;
      if (Array.isArray(seoData.keywords)) seoData.keywords = layer.keywords;
      this.addSeoWarning(product, seoData, 'layer', layer.removed,
        `상품명·검색태그의 «${[...new Set(layer.removed.map(w => String(w).trim()))].join('·')}»가 남긴 옵션(단층)과 달라 뺐습니다(1688 원래 제목의 표현). 실제 구조가 맞으면 직접 적어 저장하세요.`);
    }
  },

  promptForSeo(product, options = {}) {
    const targetKeyword = options.targetKeyword || product.targetKeyword || '';

    // 브랜드명 처리 - 쉼표로 구분된 경우 첫 번째 값만 사용
    let brandName = options.brandName || product.brand || '';
    if (brandName && brandName.includes(',')) {
      brandName = brandName.split(',')[0].trim();
      console.log('쉼표로 구분된 브랜드명에서 첫 번째 값만 사용:', brandName);
    }

    const productFeature = options.productFeature || '';
    const relatedKeywords = options.relatedKeywords || [];
    const allOptionsSummary = options.allOptionsSummary || '';

    console.log("SEO 프롬프트 - 입력 데이터:", {
      productName: product.productName,
      productNameTrans: product.productNameTrans,
      targetKeyword: options.targetKeyword || product.targetKeyword,
      brand: options.brandName || product.brand,
      optionsCount: product.options ? product.options.length : 0
    });
    // 옵션 정보 수집 개선
    let optionsInfo = '';
    if (product.options && product.options.length > 0) {
      optionsInfo = '\n옵션 정보:\n';
      product.options.forEach((option, index) => {
        // option1과 option2 정보 추출
        const option1Info = option.option1 ? `${option.option1.name || ''} (${option.option1.nameTrans || ''})` : '';
        const option2Info = option.option2 ? `${option.option2.name || ''} (${option.option2.nameTrans || ''})` : '';

        // 옵션 정보 구성
        optionsInfo += `${index + 1}. 옵션번호: ${option.optionNumber}\n`;
        if (option1Info) optionsInfo += `   옵션1: ${option1Info}\n`;
        if (option2Info) optionsInfo += `   옵션2: ${option2Info}\n`;
      });
    }

    // 1688 속성 중 소재·종류·모양 — 옵션 이름(白玉=옥빛 색 이름)만 보고 소재를 짐작하지 않게(PR261016-8C6EFC)
    // 속성 이름은 원문(材质)일 수도, 이미 번역된 이름(원단·제품 카테고리)일 수도 있다(PR261016-6E43DF)
    const ATTR_FOR_NAME = /材质|材料|面料|产品类别|类别|造型|形状|款式|소재|재질|원단|카테고리|품목명|종류|모양|형태|material|fabric|category|shape/i;
    const attrLines = (Array.isArray(product.attributes) ? product.attributes : [])
      .filter(a => a && ATTR_FOR_NAME.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`) && a.value && a.value !== '/')
      .slice(0, 6)
      .map(a => `${a.attributeNameTrans || a.attributeName}: ${a.value}${a.valueTrans ? ` (${a.valueTrans})` : ''}`);

    // 연관 키워드 정보 추가
    let relatedKeywordsSection = '';
    if (relatedKeywords && relatedKeywords.length > 0) {
      relatedKeywordsSection = `
  쿠팡에서 수집된 연관 키워드:
  ${relatedKeywords.join(', ')}

  위 연관 키워드는 이 상품과 실제로 맞는 것만 쓰세요. 상품에 없는 기능·부품·용도 단어(예: 상품에 없는 '자석 도어락')는 쓰지 마세요.
  `;
    }

    const categoryPath = this.categoryPathOf(product);
    // 일반 상품에 효능·의료 표현을 쓰면 법 위반 소지(PR261017-D188A7) — 의료기기 카테고리만 예외
    const efficacyRule = isMedicalCategory(categoryPath)
      ? ''
      : '- NO MEDICAL/EFFICACY CLAIMS: this is NOT a medical device. NEVER write pain relief, treatment or disease words (통증, 통증 완화, 치료, 염증, 질환, 혈액순환, 족저근막염 …) in the product name or search tags (product-type nouns like 자세 교정, 체형 교정 are fine), even if the Related Keywords contain them (e.g., use 쿠션 깔창, not 통증 완화 깔창)';
    const kidsRule = categoryPath && !isKidsCategory(categoryPath)
      ? `
- This product is NOT a children's product (category: ${categoryPath}). NEVER use 어린이, 아동, 키즈, 유아 (or 어린이용·아동용·키즈용·유아용) in the product name, even if the Original Product Name or Related Keywords contain them`
      : '';

    // 1688(중국 공급처) 상품 — 国产 은 중국 기준 '자국산' = 중국산이다. 원산지 한국 주장은 오표시(PR261017-2A7068)
    // 한국 도매처 상품(원 제목 한글·링크 1688 아님)엔 «made in CHINA» 를 붙이지 않는다 — 원산지 줄만 빼고 대상 줄은 그대로
    const originLine = isChinaSourced({ originalTitle: product.productName, purchaseLink: product['구매링크'] })
      ? `
- ORIGIN: This product is sourced from 1688 (made in CHINA). 国产/国内 in the source means made in CHINA (China's "domestic"), NOT Korea. NEVER write 국산, 국내산, 한국산, 국내생산, 메이드인코리아 or "Made in Korea" in the product name or search tags, even if the Related Keywords contain them (e.g., use 광목주머니, not 국산광목주머니)`
      : '';
    const originRule = `${originLine}
- AUDIENCE: Do not add child-target words (여아, 남아, 유아, 아동, 어린이, 키즈, 아기) from the Related Keywords unless the Original Product Name/Attributes/Options/Product Features say it is for children (少女风 = girlish style, NOT for girls)`;

    // 상품 특징에서 «쓰지 말 것» 한 낱말 — 프롬프트에도 못 박는다(PR261017-E63D88)
    const bannedWords = bannedPhrasesFromFeature(productFeature, targetKeyword);
    // 키워드가 금지어와 겹치면 «이 키워드를 넣어라»를 빼고 금지어 줄로만 알린다(PR261018-3DBE82)
    const promptKeyword = bannedWords.includes(String(targetKeyword).replace(/\s+/g, '')) ? '(none — the seller forbade it; do NOT put it in the name)' : targetKeyword;
    // 남긴 옵션만 등록하거나(소싱 keep) 사용자가 구성을 적었으면, 1688 원 제목의 개수·구성은 다른 SKU 것일 수 있다(PR261016-423097 · PR261016-51A981)
    const compositionFixed = (Array.isArray(product.sourcingSkuIds) && product.sourcingSkuIds.length > 0) || !!String(productFeature).trim();
    const originalNameLabel = compositionFixed ? 'Original Product Name (reference only — DO NOT take counts/quantities/composition/shape from it)' : 'Original Product Name';
    const countRule = compositionFixed
      ? `
- COUNTS/COMPOSITION/SHAPE (개입, 개, pcs, 구, 세트 수량, item type, shape words like 반손가락/half-finger) MUST follow the Options above${productFeature ? ' and the Product Features' : ''}. The Original Product Name describes the 1688 listing (other SKUs may have other counts/items) — never copy its counts. The Target Keyword is a search keyword, not proof of the item: if Options${productFeature ? '/Product Features' : ''} describe a different item, name the item they describe. If unsure, leave counts out.
- COLOR MUST follow the Options above. If the Original Product Name names a color (e.g., 金色 = gold) that the Options do not have, do NOT put that color in the product name or keywords — never take colors from the Original Product Name.
- Translate option values from the option text itself, not from the Original Product Name (e.g., 大半掌 = covers fingers with a half-length palm — it is NOT 半指 반손가락/반장갑).
- A storage CAPACITY in the Options (e.g., 可放200张 = holds 200 sheets) is NOT a quantity sold: write it with its meaning (e.g., "200매 수납") or leave it out — never a bare "200매".`
      : '';

    return `You are a Korean e-commerce SEO specialist for Coupang marketplace. Generate SEO-optimized Korean product information based on the following product data:

PRODUCT INFORMATION:
- Product Features: ${productFeature}${bannedWords.length ? `
- NEVER use these words in the product name, keywords or option names (the seller forbade them — even if the Target Keyword, Original Product Name or 1688 Attributes contain them): ${bannedWords.join(', ')}` : ''}
- ${originalNameLabel}: ${product.productName || product.productNameTrans || ''}
- Brand: ${brandName}
- Target Keyword: ${promptKeyword}
- Options: ${optionsInfo}${attrLines.length ? `
- 1688 Attributes (material/type/shape of THIS product): ${attrLines.join(' · ')}` : ''}${allOptionsSummary ? `
- ALL option values of this product (the list above may be partial):
${allOptionsSummary}
` : ''}
${this.bundleQuantityOf(product) ? `- Bundle Quantity: ${this.bundleQuantityOf(product)} (this listing sells ${this.bundleQuantityOf(product)} pieces as one unit — put "${this.bundleQuantityOf(product)}개" in the product name)
` : ''}- Related Keywords: ${relatedKeywordsSection}

REQUIREMENTS:

1. KOREAN PRODUCT NAME (Max 70 characters):
- Create a natural Korean product name that includes core keywords
- MUST be in Korean only - NO Chinese characters, NO foreign languages
- Use keyword combinations, avoid excessive adjectives
- Include brand name, product features, and target keywords
- DO NOT list multiple option names in the product name (e.g., avoid "스크레처볼 9cm 12cm 16cm")
- DO NOT put some option values in parentheses (e.g., avoid "(암적색/암적색 체크)") — the product is sold in ALL listed option values, so naming a few misrepresents it
- The Original Product Name and Related Keywords are reference only: use a word only if it truly describes THIS product. Never add features/parts the product does not have
- Focus on the main product category and key features only
- FUNCTION/EFFICACY claims (자외선 차단, 쿨링/냉감, 자동 털제거, 항균, 방수 …) only if the 1688 Attributes, Options or Product Features state them. Words in the Original Product Name (防晒, 冰丝, 一键, 真丝) are seller marketing, NOT proof. If the material is unknown, name no material. Never put composition percentages (e.g., 폴리에스터 96%) in the product name.
- MATERIAL words (옥, 실크, 가죽, 메탈 …) MUST match the 1688 Attributes material when given. Color/style names in options are NOT materials (e.g., 白玉/青玉 = jade-like white/green COLOR, not 옥 when the material is 亚克力 acrylic). Translate flower/motif names exactly from the option text, using its English in parentheses as a check (e.g., 铃兰 = 은방울꽃, NOT 벚꽃).
- Start the product name with the Brand exactly as given ("${brandName}") — never alter its letters. Never put supplier item codes (e.g., F1467, FZ-130) in the product name.
- The NUMBER of options listed is NOT a pack count: never write "N개" because N options are listed. A count in the name must come from a count in the option text, the Product Features or the bundle quantity.
${efficacyRule}
- The ITEM TYPE (the main noun, e.g., 바라클라바, 헤어밴드, 집게핀) MUST be the item of the Target Keyword / 1688 product category. Never switch to a different item named in the Original Product Name or Related Keywords (e.g., a 바라클라바 is NOT a 헤어밴드 or 머리띠, even if 头巾 or 헤어밴드 appears there).
- Do NOT use character or nickname words (e.g., 人鱼姬 인어공주, 公主 공주, 곰돌이) unless the Options or 1688 Attributes show that motif — in the Original Product Name or Related Keywords they are usually a style nickname.
- SIZES/SPECS (inch, cm, mm, ml) in the product name MUST come only from the Options above. The Original Product Name may mention sizes that are NOT sold here — never copy those. If unsure, leave sizes out.${kidsRule}${originRule}${countRule}
- The Options listed above are the ONLY variants being sold. Do NOT put any color, pattern, design or character from the Original Product Name that does not appear in the Options.
- If Product Features are given, base the name on Product Features first; use the Original Product Name only for the product category.
- Example: "쿠플러스 고양이 장난감 스크레처볼" (Good) vs "쿠플러스 스크레처볼 9cm 12cm 16cm 19cm" (Bad)

2. SEARCH KEYWORDS (Generate as many as possible up to 150 characters):
- Generate MAXIMUM possible Korean keywords for search optimization
- Use the provided related keywords that match THIS product (skip ones describing features/parts it does not have) and add more relevant ones
- Include variations, synonyms, and related terms
- Exclude competitor brand names
- NO Chinese characters or Chinese words
- Fill up to 150 characters including commas - generate as many keywords as possible
- Priority: quantity over brevity (system will trim if needed)

3. OPTION TRANSLATIONS:
- CRITICAL: All option names MUST be in Korean only - ABSOLUTELY NO Chinese characters
- NEVER copy Chinese characters directly - always translate to Korean
- Translate each option to natural Korean names using Korean words only
- Combine option1 and option2 information into a single Korean option name
- IMPORTANT: If multiple values are present (e.g., "250ml/300ml", "9cm/12cm"), choose ONLY ONE representative value
- Use the first or most common value, avoid listing multiple options (e.g., "250ml" not "250ml/300ml")
- Keep option names simple and single-valued for registration compatibility

TRANSLATION RULES:
- Color translations (MANDATORY Korean): black→블랙, white→화이트, red→레드, blue→블루, green→그린, yellow→옐로우, purple→퍼플, pink→핑크, gray→그레이, brown→브라운, beige→베이지, silver→실버, gold→골드
- Size conversions: Convert to Korean sizes (斤 to kg, use Korean numbers: 1→1개, 2→2개, etc.)
- Material translations: 棉→면, 丝→실크, 皮→가죽, 塑料→플라스틱, 金属→메탈
- Whole terms first (never split into single characters): 长筒→장목, 中筒→중목, 短筒→단목, 毛巾底→타월 바닥
- Common Chinese to Korean: 大→대형, 中→중형, 小→소형, 长→긴, 短→짧은
- Numbers: 一→1, 二→2, 三→3, 四→4, 五→5, 六→6, 七→7, 八→8, 九→9, 十→10 — ONLY when followed by a unit (个/只/件/套/包/装). Inside a word they are NOT counts: 三通 = 3방향/T자 분기, 双头 = 양방향
- Units: 个→개, 只→개, 件→개, 双→켤레, 对→쌍, 套→세트, 包→팩

CRITICAL RULES FOR OPTION TRANSLATION:
- NEVER use Chinese characters like: 红色, 蓝色, 绿色, 黄色, 紫色, 粉色, 黑色, 白色
- NEVER use Chinese characters like: 大号, 中号, 小号, 特大, 加大
- NEVER use Chinese characters like: 长款, 短款, 厚款, 薄款
- NEVER use Chinese characters like: 圆形, 方形, 椭圆, 三角
- ALWAYS translate to Korean: 빨강, 파랑, 초록, 노랑, 보라, 분홍, 검정, 하양
- ALWAYS translate to Korean: 대형, 중형, 소형, 특대형, 초대형
- ALWAYS translate to Korean: 긴형, 짧은형, 두꺼운형, 얇은형
- ALWAYS translate to Korean: 원형, 사각형, 타원형, 삼각형

If you see ANY Chinese character in your translation, STOP and replace it with Korean equivalent immediately

CRITICAL RULES:
- Product name should describe the MAIN PRODUCT only, not list all size/color variations
- Keep product name focused and concise
- ALL TEXT MUST BE IN KOREAN ONLY - ABSOLUTELY NO CHINESE CHARACTERS
- Option names MUST be translated to Korean - never keep original Chinese
- NEVER use multiple values in option names (e.g., "250ml/300ml" → "250ml", "9cm/12cm" → "9cm")
- Choose ONE representative value per option for registration system compatibility
- If you see Chinese characters in your response, replace them with Korean equivalents
- Double-check every word to ensure it's Korean, not Chinese
- Use Korean descriptive words when direct translation is unclear

Respond in this exact JSON format (ALL TEXT IN KOREAN):
{
  "productName": "Korean optimized product name (NO Chinese characters)",
  "keywords": ["Korean keyword1", "Korean keyword2", "Korean keyword3"],
  "options": [
    {
      "optionNumber": "keep original option number exactly",
      "korName": "KOREAN ONLY option name - translate all Chinese to Korean"
    }
  ]
}

FINAL CHECK: Before responding, verify that:
1. Product name contains NO Chinese characters (check every character)
2. All keywords are in Korean (check every keyword)
3. ALL option korName values are in Korean only (check every option name character by character)
4. No Chinese characters exist anywhere in the JSON response
5. If you find ANY Chinese character, replace it with Korean equivalent immediately

SPECIFIC CHINESE CHARACTERS TO ELIMINATE:
- Colors: 红色→빨강, 蓝色→파랑, 绿色→초록, 黄色→노랑, 紫色→보라, 粉色→분홍, 黑色→검정, 白色→하양
- Sizes: 大号→대형, 中号→중형, 小号→소형, 特大→특대형, 加大→초대형
- Styles: 长款→긴형, 短款→짧은형, 厚款→두꺼운형, 薄款→얇은형
- Shapes: 圆形→원형, 方形→사각형, 椭圆→타원형, 三角→삼각형
- Common: 个→개, 只→개, 件→개, 双→켤레, 对→쌍, 套→세트, 包→팩
- Numbers: 一→1, 二→2, 三→3, 四→4, 五→5, 六→6, 七→7, 八→8, 九→9, 十→10 — ONLY when followed by a unit (个/只/件/套/包/装). Inside a word they are NOT counts: 三通 = 3방향/T자 분기, 双头 = 양방향

6. Scan your entire response for these Chinese characters and replace them
7. Double-check that ALL option names are 100% Korean
8. If you see characters like 红, 蓝, 绿, 黄, 紫, 粉, 黑, 白, 大, 中, 小, 长, 短 - REPLACE IMMEDIATELY`;
  },

  /**
   * 중국어 문자 제거 및 한국어 변환 (포괄적 버전)
   * @param {string} text - 변환할 텍스트
   * @returns {string} 한국어로 변환된 텍스트
   */
  removeChinese(text) {
    if (!text || typeof text !== 'string') return text;

    // 포괄적 중국어 → 한국어 매핑 (간체자, 번체자, 한자 모두 포함)
    const chineseToKorean = {
      // 색상 (Colors)
      '红色': '빨강', '蓝色': '파랑', '绿色': '초록', '黄色': '노랑', '紫色': '보라',
      '粉色': '분홍', '黑色': '검정', '白色': '하양', '灰色': '회색', '橙色': '주황',
      '红': '빨강', '蓝': '파랑', '绿': '초록', '黄': '노랑', '紫': '보라',
      '粉': '분홍', '黑': '검정', '白': '하양', '灰': '회색', '橙': '주황',
      '藍': '파랑', '綠': '초록', '黃': '노랑', '紅': '빨강', // 번체자

      // 크기 (Sizes)
      '大号': '대형', '中号': '중형', '小号': '소형', '特大': '특대형', '加大': '초대형',
      '超大': '초대형', '迷你': '미니', '大': '대형', '中': '중형', '小': '소형',
      '特': '특', '加': '대형', '超': '초대형', '號': '호', // 번체자

      // 스타일 (Styles)
      '长款': '롱타입', '短款': '숏타입', '厚款': '두꺼운타입', '薄款': '얇은타입',
      '长筒': '장목', '中筒': '중목', '短筒': '단목', '毛巾底': '타월 바닥', // 옵션 낱말 표(seoSpecGuard OPTION_TERMS)와 같은 말 · 긴 낱말 먼저(sortedEntries)
      '长': '긴', '短': '짧은', '厚': '두꺼운', '薄': '얇은',
      '長': '긴', // 번체자

      // 모양 (Shapes)
      '圆形': '원형', '方形': '사각형', '椭圆': '타원형', '三角': '삼각형',
      '长方形': '직사각형', '圆': '원형', '方': '사각형', '椭': '타원',
      '圓': '원형', '橢': '타원', // 번체자

      // 단위 (Units)
      '个': '개', '只': '개', '件': '개', '双': '쌍', '对': '쌍', '套': '세트',
      '包': '팩', '盒': '박스', '瓶': '병', '罐': '캔', '袋': '봉지',
      '雙': '쌍', '對': '쌍', // 번체자

      // 숫자 (Numbers)
      '一': '일', '二': '이', '三': '삼', '四': '사', '五': '오',
      '六': '육', '七': '칠', '八': '팔', '九': '구', '十': '십',
      '壹': '일', '贰': '이', '叁': '삼', '肆': '사', '伍': '오', // 대문자 숫자
      '陆': '육', '柒': '칠', '捌': '팔', '玖': '구', '拾': '십',

      // 재질 (Materials)
      '棉': '면', '丝': '실크', '皮': '가죽', '塑料': '플라스틱', '金属': '금속',
      '木': '나무', '竹': '대나무', '玻璃': '유리', '陶瓷': '도자기',
      '絲': '실크', '塑膠': '플라스틱', '金屬': '금속', // 번체자

      // 신체 부위 (Body parts)
      '露指': '손가락노출형', '无指': '손가락없음', '半指': '반손가락',
      '全指': '전체손가락', '無指': '손가락없음', // 번체자

      // 일반 단어 (Common words)
      '款式': '스타일', '型号': '모델', '规格': '규격', '尺寸': '사이즈', '重量': '무게',
      '颜色': '색상', '材质': '소재', '品牌': '브랜드', '价格': '가격',
      '款': '타입', '型': '형', '号': '호', '式': '식', '质': '질',
      '規格': '규격', '顏色': '색상', '材質': '소재', '價格': '가격', // 번체자

      // 기능 (Functions)
      '防水': '방수', '透气': '통기성', '保温': '보온', '散热': '방열',
      '防滑': '미끄럼방지', '耐磨': '내마모성', '抗菌': '항균',
      '透氣': '통기성', '保溫': '보온', // 번체자

      // 특수 상품명
      '剑麻球': '사이잘볼', '剑麻': '사이잘', '瑜伽': '요가', '健身': '헬스',
      '劍麻': '사이잘', // 번체자

      // 기타 자주 사용되는 한자
      '新': '신', '老': '구', '好': '좋은', '坏': '나쁜', '高': '높은', '低': '낮은',
      '快': '빠른', '慢': '느린', '热': '뜨거운', '冷': '차가운', '软': '부드러운', '硬': '딱딱한',
      '輕': '가벼운', '重': '무거운', '舊': '오래된', // 번체자
      '熱': '뜨거운', '軟': '부드러운',

      // 일본어 히라가나 (자주 사용되는 것들)
      'あ': '아', 'い': '이', 'う': '우', 'え': '에', 'お': '오',
      'か': '카', 'き': '키', 'く': '쿠', 'け': '케', 'こ': '코',
      'さ': '사', 'し': '시', 'す': '스', 'せ': '세', 'そ': '소',
      'た': '타', 'ち': '치', 'つ': '츠', 'て': '테', 'と': '토',
      'な': '나', 'に': '니', 'ぬ': '누', 'ね': '네', 'の': '노',
      'は': '하', 'ひ': '히', 'ふ': '후', 'へ': '헤', 'ほ': '호',
      'ま': '마', 'み': '미', 'む': '무', 'め': '메', 'も': '모',
      'や': '야', 'ゆ': '유', 'よ': '요',
      'ら': '라', 'り': '리', 'る': '루', 'れ': '레', 'ろ': '로',
      'わ': '와', 'ゐ': '위', 'ゑ': '웨', 'を': '오', 'ん': '응',

      // 일본어 가타카나 (자주 사용되는 것들)
      'ア': '아', 'イ': '이', 'ウ': '우', 'エ': '에', 'オ': '오',
      'カ': '카', 'キ': '키', 'ク': '쿠', 'ケ': '케', 'コ': '코',
      'サ': '사', 'シ': '시', 'ス': '스', 'セ': '세', 'ソ': '소',
      'タ': '타', 'チ': '치', 'ツ': '츠', 'テ': '테', 'ト': '토',
      'ナ': '나', 'ニ': '니', 'ヌ': '누', 'ネ': '네', 'ノ': '노',
      'ハ': '하', 'ヒ': '히', 'フ': '후', 'ヘ': '헤', 'ホ': '호',
      'マ': '마', 'ミ': '미', 'ム': '무', 'メ': '메', 'モ': '모',
      'ヤ': '야', 'ユ': '유', 'ヨ': '요',
      'ラ': '라', 'リ': '리', 'ル': '루', 'レ': '레', 'ロ': '로',
      'ワ': '와', 'ヰ': '위', 'ヱ': '웨', 'ヲ': '오', 'ン': '응',

      // 일본어 단어 (자주 나오는 것들)
      'なし': '없음', 'あり': '있음', 'サイズ': '사이즈', 'カラー': '색상',
      'タイプ': '타입', 'モデル': '모델', 'スタイル': '스타일',
      'ブランド': '브랜드', 'セット': '세트', 'パック': '팩'
    };

    let result = text;

    // 중국어 문자 치환 (긴 문자열부터 처리하여 부분 매칭 방지)
    const sortedEntries = Object.entries(chineseToKorean).sort((a, b) => b[0].length - a[0].length);

    sortedEntries.forEach(([chinese, korean]) => {
      result = result.replace(new RegExp(chinese, 'g'), korean);
    });

    // 남은 중국어 문자 패턴 감지 및 제거 (유니코드 범위 기반)
    // 중국어 유니코드 범위: \u4e00-\u9fff (CJK 통합 한자)
    // 하지만 한국어 한자도 포함되므로 신중하게 처리

    return result;
  },

  /**
   * AI를 통한 중국어 검증 및 재번역
   * @param {Object} seoData - SEO 응답 데이터
   * @returns {Promise<Object>} 중국어가 제거된 SEO 데이터
   */
  async validateAndTranslateWithAI(seoData, sourceOptions) {
    if (!seoData) return seoData;

    try {
      console.log('🤖 AI를 통한 중국어 검증 및 재번역 시작');

      // 검증할 데이터 준비
      const dataToValidate = {
        productName: seoData.productName || '',
        keywords: seoData.keywords || [],
        options: seoData.options || []
      };

      // AI 검증 프롬프트 생성
      const validationPrompt = this.createValidationPrompt(dataToValidate, sourceOptions);

      // AI에게 검증 요청
      const validationResult = await workflowService.requestExtensionWithPromise(
        {
          workType: 'SEO_VALIDATION',
          task: 'VALIDATE_CHINESE',
          requestArr: [{ prompt: validationPrompt }]
        },
        { timeout: 30000, maxRetries: 1 }
      );

      console.log('🔍 AI 검증 결과:', validationResult);

      // 검증 결과 파싱
      const validatedData = this.parseValidationResult(validationResult);

      if (validatedData) {
        console.log('✅ AI 검증 및 재번역 완료');

        // 옵션 번호 정규화 적용
        let normalizedOptions = validatedData.options ? validatedData.options.map(option => ({
          ...option,
          optionNumber: this.normalizeOptionNumber(option.optionNumber)
        })) : seoData.options;
        // 검증 답의 옵션 번호 모둠이 보낸 것과 다르면(빠짐·겹침·다른 번호) 옵션은 검증 전 그대로 — 옵션끼리 이름이 뒤섞인다(PR261021-3D32BB)
        const numsOf = (arr) => (arr || []).map(o => String(o && o.optionNumber)).sort().join('\n');
        if (validatedData.options && numsOf(normalizedOptions) !== numsOf(seoData.options)) {
          console.warn('⚠️ AI 검증 옵션 번호가 보낸 것과 달라 옵션은 검증 전 그대로 둡니다');
          normalizedOptions = seoData.options;
        }

        return {
          ...seoData,
          productName: validatedData.productName || seoData.productName,
          keywords: validatedData.keywords || seoData.keywords,
          options: normalizedOptions
        };
      } else {
        console.warn('⚠️ AI 검증 결과 파싱 실패, 기존 데이터 사용');
        return this.cleanChineseFromSeoData(seoData);
      }

    } catch (error) {
      console.error('❌ AI 검증 중 오류:', error);
      console.log('🔄 기존 중국어 제거 로직으로 폴백');
      logAIRequestError('seoService', 'verifyAndCleanWithAI', error, {
        fallbackAction: '기존 중국어 제거 로직으로 폴백',
        productId: seoData?.productId,
        additional: { hasChineseCheck: true }
      });
      return this.cleanChineseFromSeoData(seoData);
    }
  },

  /**
   * AI 검증용 프롬프트 생성
   * @param {Object} data - 검증할 데이터
   * @returns {string} 검증 프롬프트
   */
  createValidationPrompt(data, sourceOptions) {
    // 옵션마다 원문(1688)을 같이 싣는다 — 한글만 보면 옵션끼리 뜻을 바꿔 옮긴다(PR261021-3D32BB 长筒/中筒)
    const srcOf = (opt) => {
      const s = Array.isArray(sourceOptions) && sourceOptions.find(x => x && x.optionNumber === opt.optionNumber);
      const n = s ? ['option1', 'option2'].map(k => (s[k] && s[k].name) || '').filter(Boolean).join(' / ') : '';
      return n ? ` (original: "${n}")` : '';
    };
    return `You are a Korean language validator. Check the following Korean e-commerce data for ANY Chinese characters, Japanese characters, and Chinese-origin characters, then translate them to Korean.

CRITICAL TASK: Find and translate ALL Chinese characters, Japanese characters, and Chinese-origin characters to Korean

DATA TO CHECK:
Product Name: "${data.productName}"

Keywords: ${JSON.stringify(data.keywords)}

Options:
${data.options.map((opt, idx) => `${idx + 1}. Option ${opt.optionNumber}: "${opt.korName}"${srcOf(opt)}`).join('\n')}

INSTRUCTIONS:
1. Scan EVERY character in the product name, keywords, and option names
2. Find ANY Chinese or Japanese characters, including:
   - Simplified Chinese characters (简体字)
   - Traditional Chinese characters (繁體字)
   - Japanese Hiragana (ひらがな): あ, い, う, え, お, か, き, etc.
   - Japanese Katakana (カタカナ): ア, イ, ウ, エ, オ, カ, キ, etc.
   - Japanese Kanji (漢字): 指, 防水, 通気, etc.
   - Chinese-origin characters that look similar to Chinese
   - Any character that appears to be Chinese, Japanese, or Chinese-like
3. Translate ALL found Chinese/Japanese/Chinese-like characters to natural Korean
4. Keep all Korean text unchanged
5. Maintain the same structure and meaning
6. Be thorough - even single characters that might be Chinese or Japanese should be translated
7. Each option's Korean name must keep the meaning of its OWN original text — never move words between options, keep every optionNumber exactly as given

COMPREHENSIVE TRANSLATION EXAMPLES:
Colors: 红色→빨강, 蓝色→파랑, 绿色→초록, 黄色→노랑, 紫色→보라, 粉色→분홍, 黑色→검정, 白色→하양, 灰色→회색, 橙色→주황
Sizes: 大号→대형, 中号→중형, 小号→소형, 特大→특대, 加大→대형, 超大→초대형
Quantities: 个→개, 只→개, 件→개, 双→쌍, 对→쌍, 套→세트, 包→팩, 盒→박스
Numbers: 一→일, 二→이, 三→삼, 四→사, 五→오, 六→육, 七→칠, 八→팔, 九→구, 十→십
Materials: 棉→면, 丝→실크, 皮→가죽, 塑料→플라스틱, 金属→금속, 木→나무, 竹→대나무
Shapes: 圆形→원형, 方形→사각형, 椭圆→타원형, 三角→삼각형, 长方形→직사각형
Styles: 长款→롱타입, 短款→숏타입, 厚款→두꺼운타입, 薄款→얇은타입
Body parts: 露指→손가락노출형, 无指→손가락없음, 半指→반손가락
Common words: 款式→스타일, 型号→모델, 规格→규격, 尺寸→사이즈, 重量→무게
Actions: 防水→방수, 透气→통기성, 保温→보온, 散热→방열
Japanese Hiragana: あ→아, い→이, う→우, え→에, お→오, か→카, き→키, く→쿠, け→케, こ→코
Japanese Katakana: ア→아, イ→이, ウ→우, エ→에, オ→오, カ→카, キ→키, ク→쿠, ケ→케, コ→코
Japanese words: なし→없음, あり→있음, サイズ→사이즈, カラー→색상, タイプ→타입, モデル→모델

Respond in this exact JSON format:
{
  "productName": "corrected Korean product name (all Chinese/Chinese-like characters translated)",
  "keywords": ["corrected Korean keyword1", "corrected Korean keyword2", ...],
  "options": [
    {
      "optionNumber": "clean option number without 'Option ' prefix",
      "korName": "corrected Korean option name (all Chinese/Chinese-like characters translated)"
    }
  ]
}

IMPORTANT RULES:
1. If NO Chinese, Japanese, or Chinese-like characters are found, return the original data unchanged
2. If ANY Chinese, Japanese, or Chinese-like characters ARE found, translate ALL of them to appropriate Korean equivalents
3. Be extremely thorough - translate even single characters that might be Chinese or Japanese
4. Examples of what to translate:
   - 露指 → 손가락 노출형
   - 红色 → 빨강색
   - 大号 → 대형
   - 个 → 개
   - 款 → 타입
   - 型 → 형
   - あ → 아
   - カ → 카
   - なし → 없음
   - サイズ → 사이즈
5. Remove "Option " prefix from optionNumber if present (e.g., "Option 250531011001" → "250531011001")
6. Keep optionNumber as pure numbers/codes without any text prefixes
7. Ensure ALL text in productName, keywords, and option names is in natural Korean`;
  },

  /**
   * AI 검증 결과 파싱
   * @param {Object} result - AI 검증 결과
   * @returns {Object|null} 파싱된 데이터
   */
  parseValidationResult(result) {
    try {
      let validatedData;

      // batchResults 구조 처리
      if (result && result.batchResults && Array.isArray(result.batchResults) && result.batchResults.length > 0) {
        const firstBatch = result.batchResults[0];

        if (firstBatch.result && typeof firstBatch.result === 'object') {
          validatedData = firstBatch.result;
        } else if (firstBatch.processedText) {
          // JSON 블록 제거 후 파싱
          let jsonText = firstBatch.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          validatedData = JSON.parse(jsonText);
        }
      } else if (typeof result === 'string') {
        validatedData = JSON.parse(result);
      } else if (typeof result === 'object') {
        validatedData = result;
      }

      // 필수 필드 확인
      if (validatedData && validatedData.productName && validatedData.keywords && validatedData.options) {
        return validatedData;
      }

      return null;
    } catch (error) {
      console.error('AI 검증 결과 파싱 오류:', error);
      return null;
    }
  },

  /**
   * SEO 응답 데이터에서 중국어 제거 (폴백용)
   * @param {Object} seoData - SEO 응답 데이터
   * @returns {Object} 중국어가 제거된 SEO 데이터
   */
  cleanChineseFromSeoData(seoData) {
    if (!seoData) return seoData;

    const cleaned = { ...seoData };

    // 상품명 중국어 제거
    if (cleaned.productName) {
      cleaned.productName = this.removeChinese(cleaned.productName);
    }

    // 키워드 중국어 제거
    if (cleaned.keywords && Array.isArray(cleaned.keywords)) {
      cleaned.keywords = cleaned.keywords.map(keyword => this.removeChinese(keyword));
    }

    // 옵션명 중국어 제거
    if (cleaned.options && Array.isArray(cleaned.options)) {
      cleaned.options = cleaned.options.map(option => ({
        ...option,
        korName: this.removeChinese(option.korName)
      }));
    }

    console.log('🔧 중국어 제거 전후 비교:', {
      original: seoData,
      cleaned: cleaned
    });

    return cleaned;
  },

  /**
   * SEO 점수 계산
   * @param {string} productName - 상품명
   * @param {Array} keywords - 키워드 배열
   * @param {string} metaDescription - 메타 설명
   * @param {Array} features - 특징 배열
   * @returns {Object} 점수 데이터
   */
  calculateSeoScore(productName, keywords, metaDescription, features) {
    // 각 항목별 점수 계산
    let productNameScore = 0;
    let keywordsScore = 0;
    let metaDescriptionScore = 0;
    let featuresScore = 0;

    // 상품명 점수 (길이와 키워드 포함 여부 평가)
    if (productName) {
      // 적정 길이 (30-70자) 체크
      const nameLength = productName.length;
      if (nameLength >= 30 && nameLength <= 70) {
        productNameScore = 100;
      } else if (nameLength >= 20 && nameLength < 30) {
        productNameScore = 80;
      } else if (nameLength > 70 && nameLength <= 90) {
        productNameScore = 70;
      } else {
        productNameScore = 50;
      }

      // 키워드 포함 여부로 점수 추가 조정 가능
      if (keywords && keywords.length > 0) {
        const keywordIncluded = keywords.some(keyword =>
          productName.toLowerCase().includes(keyword.toLowerCase())
        );
        if (keywordIncluded) {
          productNameScore = Math.min(100, productNameScore + 10);
        }
      }
    }

    // 키워드 점수 (개수와 품질 평가)
    if (keywords && keywords.length > 0) {
      // 적정 개수 (5-10개) 체크
      const keywordCount = keywords.length;
      if (keywordCount >= 5 && keywordCount <= 10) {
        keywordsScore = 100;
      } else if (keywordCount >= 3 && keywordCount < 5) {
        keywordsScore = 80;
      } else if (keywordCount > 10 && keywordCount <= 15) {
        keywordsScore = 70;
      } else {
        keywordsScore = 50;
      }

      // 키워드 품질 (길이, 중복성 등) 평가
      // 평균 키워드 길이 계산
      const avgKeywordLength = keywords.reduce((sum, keyword) => sum + keyword.length, 0) / keywordCount;
      // 키워드 길이가 적절한지 체크 (2-20자)
      if (avgKeywordLength >= 2 && avgKeywordLength <= 20) {
        keywordsScore = Math.min(100, keywordsScore + 5);
      } else if (avgKeywordLength < 2 || avgKeywordLength > 30) {
        keywordsScore = Math.max(0, keywordsScore - 10);
      }
    }

    // 메타 설명 점수 (길이와 품질 평가)
    if (metaDescription) {
      // 적정 길이 (100-160자) 체크
      const descLength = metaDescription.length;
      if (descLength >= 100 && descLength <= 160) {
        metaDescriptionScore = 100;
      } else if (descLength >= 80 && descLength < 100) {
        metaDescriptionScore = 80;
      } else if (descLength > 160 && descLength <= 200) {
        metaDescriptionScore = 70;
      } else {
        metaDescriptionScore = 50;
      }

      // 키워드 포함 여부, 문장 구성 등으로 점수 추가 조정
      if (keywords && keywords.length > 0) {
        const keywordIncluded = keywords.some(keyword =>
          metaDescription.toLowerCase().includes(keyword.toLowerCase())
        );
        if (keywordIncluded) {
          metaDescriptionScore = Math.min(100, metaDescriptionScore + 10);
        }
      }

      // 콜투액션(CTA) 포함 여부 체크
      const ctaPatterns = ['지금', '바로', '확인', '구매', '클릭', '쿠팡'];
      const hasCTA = ctaPatterns.some(pattern =>
        metaDescription.includes(pattern)
      );
      if (hasCTA) {
        metaDescriptionScore = Math.min(100, metaDescriptionScore + 5);
      }
    }

    // 특징 점수 (개수와 품질 평가)
    if (features && features.length > 0) {
      // 적정 개수 (3-5개) 체크
      const featureCount = features.length;
      if (featureCount >= 3 && featureCount <= 5) {
        featuresScore = 100;
      } else if (featureCount >= 1 && featureCount < 3) {
        featuresScore = 80;
      } else if (featureCount > 5 && featureCount <= 7) {
        featuresScore = 70;
      } else {
        featuresScore = 50;
      }

      // 특징 품질 (길이, 구체성 등) 평가
      const validFeatures = features.filter(feature => feature.length >= 10 && feature.length <= 100);
      const validRatio = validFeatures.length / features.length;

      if (validRatio >= 0.8) {
        featuresScore = Math.min(100, featuresScore + 10);
      } else if (validRatio < 0.5) {
        featuresScore = Math.max(0, featuresScore - 10);
      }
    }

    // 전체 점수 계산 (가중치 적용)
    const totalScore = Math.round(
      (productNameScore * 0.3) +
      (keywordsScore * 0.3) +
      (metaDescriptionScore * 0.25) +
      (featuresScore * 0.15)
    );

    // 점수 항목 구성
    const breakdown = [
      { name: '상품명 최적화', score: productNameScore },
      { name: '키워드 최적화', score: keywordsScore },
      { name: '메타 설명 품질', score: metaDescriptionScore },
      { name: '상품 특징', score: featuresScore }
    ];

    return {
      totalScore,
      breakdown
    };
  },

  /**
   * 상품명 최적화
   * @param {string} productName - 원본 상품명
   * @returns {string} 최적화된 상품명
   */
  optimizeProductName(productName) {
    if (!productName) return '';

    // 특수문자 제거 및 길이 제한

    // 특수문자 제거 (일부 특수문자 유지)
    let optimized = productName.replace(/[^\w\s가-힣\-_()]/g, ' ');

    // 여러 공백을 하나로 통합
    optimized = optimized.replace(/\s+/g, ' ').trim();

    // 70자로 길이 제한
    if (optimized.length > 70) {
      optimized = optimized.substring(0, 67) + '...';
    }

    return optimized;
  },

  /**
   * 기본 키워드 생성
   * @param {Object} product - 상품 객체
   * @returns {Array} 생성된 키워드 배열
   */
  generateDefaultKeywords(product) {
    const keywords = [];

    // 타겟 키워드 추가
    if (product.targetKeyword) {
      keywords.push(product.targetKeyword);
    }

    // 상품명에서 주요 단어 추출
    const productName = product.productName || product.productNameTrans || '';
    if (productName) {
      const words = productName
        .split(/\s+/)
        .filter(word => word.length >= 2)
        .slice(0, 5);

      keywords.push(...words);
    }

    // 브랜드 추가
    if (product.brand) {
      keywords.push(product.brand);
    }

    // 카테고리 추가
    if (product.category) {
      keywords.push(product.category);
    }

    // 추가 일반 키워드
    const additionalKeywords = ['할인', '특가', '무료배송', '로켓배송'];
    keywords.push(...additionalKeywords);

    // 중복 제거 및 최대 10개로 제한
    return [...new Set(keywords)].slice(0, 10);
  },

  /**
   * 기본 메타 설명 생성
   * @param {Object} product - 상품 객체
   * @returns {string} 생성된 메타 설명
   */
  generateDefaultDescription(product) {
    let description = '';

    // 브랜드와 상품명으로 시작
    if (product.brand) {
      description += `${product.brand} `;
    }

    const productName = product.productName || product.productNameTrans || '';
    if (productName) {
      description += `${productName}. `;
    }

    // 추가 정보
    if (product.targetKeyword) {
      description += `${product.targetKeyword} 관련 최고의 상품입니다. `;
    }

    // 기본 문구 추가
    description += '높은 품질과 합리적인 가격으로 제공됩니다. 쿠팡 로켓배송으로 빠르게 받아보세요!';

    // 160자로 길이 제한
    if (description.length > 160) {
      description = description.substring(0, 157) + '...';
    }

    return description;
  },

  /**
   * 기본 상품 특징 생성
   * @param {Object} product - 상품 객체
   * @returns {Array} 생성된 특징 배열
   */
  generateDefaultFeatures(product) {
    const productName = product.productName || product.productNameTrans || '';
    const brandName = product.brand || '제품';

    const features = [
      `${brandName}의 고품질 소재로 제작되어 내구성이 뛰어납니다.`,
      `${productName}의 세련된 디자인으로 다양한 스타일에 잘 어울립니다.`,
      `합리적인 가격으로 가성비가 우수한 상품입니다.`,
      `편리한 사용감으로 일상생활에 편의를 더합니다.`,
      `쿠팡 로켓배송으로 빠르게 받아보실 수 있습니다.`
    ];

    return features;
  },

  async handleResult(product, result) {
    try {
      if (!product || !result) return false;

      console.log('SEO 결과 처리 시작:', result);

      // 결과 파싱 시도
      let seoData;
      if (typeof result === 'string') {
        try {
          seoData = JSON.parse(result);
        } catch (e) {
          console.error('SEO 데이터 파싱 오류:', e);
          return false;
        }
      } else {
        seoData = result;
      }

      // 필수 필드 확인
      if (!seoData.productName) {
        console.error('SEO 데이터에 상품명이 없습니다.');
        return false;
      }

      console.log('처리할 SEO 데이터:', seoData);

      // 상품 정보 업데이트 (깊은 복사)
      const updatedProduct = JSON.parse(JSON.stringify(product));

      // SEO 데이터 설정
      updatedProduct.seo = {
        productName: seoData.productName,
        keywords: this.cleanSearchTagKeywords(seoData.keywords),   // 검색태그로 나간다 — 쿠팡 허용 문자만
        metaDescription: seoData.metaDescription || '',
        features: seoData.features || [],
        score: seoData.score || 0,
        scoreBreakdown: seoData.scoreBreakdown || [],
        options: seoData.options || []
      };

      // 상태 업데이트
      updatedProduct.seoStatus = 'completed';

      // coupangDoc.productName 업데이트 (옵션명 생략)
      if (updatedProduct.coupangDoc) {
        updatedProduct.coupangDoc.productName = seoData.productName;
        console.log('coupangDoc.productName 업데이트:', seoData.productName);
      }

      // 옵션 정보 업데이트
      if (seoData.options && seoData.options.length > 0 && updatedProduct.options && updatedProduct.options.length > 0) {
        console.log('옵션 정보 업데이트 시작:', seoData.options);

        // 각 옵션들을 optionNumber 기준으로 매핑하여 처리
        seoData.options.forEach(seoOption => {
          if (seoOption.optionNumber && seoOption.korName) {
            // 옵션번호로 매칭 시도
            const option = updatedProduct.options.find(opt => opt.optionNumber === seoOption.optionNumber);

            if (option) {
              console.log(`옵션 매칭 성공 - 옵션번호 ${seoOption.optionNumber}: ${seoOption.korName}`);

              // korName 속성 명시적 설정 (SEO된 옵션명)
              option.korName = seoOption.korName;

              // 옵션별 전체 상품명 생성 (SEO된 상품명 + SEO된 옵션명)
              option.fullProductName = `${updatedProduct.seo.productName}`;
              console.log(`옵션 ${seoOption.optionNumber}의 fullProductName 설정:`, option.fullProductName);

              option.seoStatus = 'completed';

              // product 레벨의 coupangDoc 업데이트 (해당 옵션의 인덱스에 맞게)
              if (updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument) {
                // 옵션 인덱스 찾기
                const optionIndex = updatedProduct.options.findIndex(opt => opt.optionNumber === seoOption.optionNumber);

                if (optionIndex !== -1 && optionIndex < updatedProduct.coupangDoc.jsonDocument.length) {
                  console.log(`product.coupangDoc.jsonDocument[${optionIndex}] 업데이트 - 옵션번호 ${seoOption.optionNumber}`);

                  // 해당 인덱스의 jsonDocument 항목 가져오기
                  const docItem = updatedProduct.coupangDoc.jsonDocument[optionIndex];

                  // startPage 상품명 업데이트 - "SEO된 productName, 옵션명" 형태로
                  if (docItem.startPage) {
                    // SEO된 옵션명으로 상품명 구성
                    const fullProductName = `${updatedProduct.seo.productName}`;
                    docItem.startPage.productName = fullProductName;
                    console.log(`옵션 ${optionIndex} startPage.productName 업데이트:`, fullProductName);
                  }

                  // 검색 태그 업데이트
                  if (docItem.productPage) {
                    // 검색 태그 길이 제한 (150자)
                    let searchTags = updatedProduct.seo.keywords.join(', ');

                    if (searchTags.length > 150) {
                      console.log(`검색 태그 길이(${searchTags.length})가 150자를 초과합니다. 태그를 줄입니다.`);

                      // 태그를 뒤에서부터 제거하면서 150자 이하로 맞추기
                      const keywords = updatedProduct.seo.keywords;
                      let trimmedKeywords = [];

                      for (let i = 0; i < keywords.length; i++) {
                        const potentialKeywords = keywords.slice(0, keywords.length - i);
                        const potentialString = potentialKeywords.join(', ');

                        if (potentialString.length <= 150) {
                          trimmedKeywords = potentialKeywords;
                          console.log(`원래 태그 수: ${keywords.length}, 줄인 태그 수: ${potentialKeywords.length}`);
                          break;
                        }
                      }

                      searchTags = trimmedKeywords.join(', ');
                      console.log(`최종 검색 태그 길이: ${searchTags.length}`);
                    }

                    docItem.productPage.searchTags = searchTags;
                  }
                } else {
                  console.warn(`옵션 인덱스(${optionIndex})에 해당하는 coupangDoc.jsonDocument 항목이 없습니다.`);
                }
              }
            } else {
              console.warn(`옵션 매칭 실패 - 옵션번호 ${seoOption.optionNumber} 찾을 수 없음`);
            }
          }
        });
      }

      // 점수 계산이 없는 경우 직접 계산
      if (!updatedProduct.seo.score) {
        const scoreData = this.calculateSeoScore(
          updatedProduct.seo.productName,
          updatedProduct.seo.keywords,
          updatedProduct.seo.metaDescription,
          updatedProduct.seo.features
        );

        updatedProduct.seo.score = scoreData.totalScore;
        updatedProduct.seo.scoreBreakdown = scoreData.breakdown;
      }

      // 상품 정보 로깅
      console.log('SEO 정보 업데이트:', {
        seoProductName: updatedProduct.seo.productName,
        keywords: updatedProduct.seo.keywords.length
      });

      // 서버에 저장 - 여기를 변경 (updateRocketProduct에서 updateProductSeo로)
      try {
        await store.dispatch('updateProductSeo', updatedProduct);
        console.log('SEO 정보 저장 성공');
        return true;
      } catch (error) {
        console.error('SEO 정보 저장 실패:', error);
        return false;
      }
    } catch (error) {
      console.error('SEO 결과 처리 중 오류:', error);
      return false;
    }
  },

  /**
   * SEO 설정 프로세스 시작
   * @param {Object} product - 상품 객체
   * @param {Object} data - 추가 데이터 (선택 사항)
   * @returns {Promise<Object>} 처리 결과
   */
  async process(product, data = {}) {
    try {
      // 상품 유효성 확인
      if (!product || !product.registrationId) {
        throw new Error('유효하지 않은 상품 정보입니다.');
      }

      // 로딩 상태 시작
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: 'SEO 최적화 시작 중...'
      });

      console.log('AI SEO 설정 모드 사용');

      // 상품 정보 기반 SEO 데이터 생성
      const options = {
        targetKeyword: product.targetKeyword || '',
        brandName: product.brand || '',
        productFeature: product.productFeature || '',
        ...data.options
      };

      // AI SEO 설정 처리 (재시도 로직 포함)
      const seoResult = await this.generateSeoWithRetry(product, options);

      // 옵션 정보 추가
      if (product.options && product.options.length > 0 && (!seoResult.options || seoResult.options.length === 0)) {
        seoResult.options = product.options.map(option => ({
          id: option.id,
          optionNumber: option.optionNumber,
          korName: option.korName || ''
        }));
      }

      // 결과 처리
      if (seoResult) {
        console.log('SEO 결과 처리:', seoResult);

        try {
          // 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
          await this.updateProductWithCoupangDocPartial(product, seoResult);
          console.log('SEO 정보로 상품 업데이트 완료');
        } catch (updateError) {
          console.error('SEO 정보로 상품 업데이트 실패:', updateError);
          if (updateError && updateError.code === 'SEO_FACT_EMPTY') throw updateError;   // 사실 검사로 이름이 빔 = SEO 실패(전송 막음)
          // 업데이트 실패해도 SEO 결과는 반환
        }

        // 작업 완료 상태 업데이트
        store.commit('ADD_COMPLETED_TASK', 'seoStatus');
        console.log('seoStatus 작업 완료 상태 업데이트됨');

        return {
          status: 'completed',
          type: 'seoStatus',
          seoInfo: seoResult
        };
      } else {
        throw new Error('SEO 데이터 생성 실패');
      }
    } catch (error) {
      console.error('SEO 처리 중 오류:', error);

      // Gemini API 에러인 경우 프로세스 중단을 위해 에러를 다시 throw
      if (error.message && error.message.startsWith('GEMINI_API_ERROR:')) {
        throw error;
      }

      return {
        status: 'error',
        type: 'seoStatus',
        error: error.message || '알 수 없는 오류'
      };
    } finally {
      // 로딩 상태 종료
      store.dispatch('setProcessing', { isProcessing: false });
    }
  },

  /**
   * 옵션 번호 정규화 (Option 접두사 제거)
   * @param {string} optionNumber - 원본 옵션 번호
   * @returns {string} 정규화된 옵션 번호
   */
  normalizeOptionNumber(optionNumber) {
    if (!optionNumber || typeof optionNumber !== 'string') {
      return optionNumber;
    }

    // "Option " 접두사 제거 (대소문자 구분 없이)
    return optionNumber.replace(/^option\s+/i, '').trim();
  },

  /**
   * 재시도 로직이 포함된 SEO 생성 메서드
   * @param {Object} product - 상품 객체
   * @param {Object} options - SEO 옵션
   * @param {number} maxRetries - 최대 재시도 횟수
   * @returns {Promise<Object>} SEO 생성 결과
   */
  async generateSeoWithRetry(product, options = {}, maxRetries = 3) {
    let geminiRetryCount = 0;
    let factEmpty = null;   // 사실 검사로 이름이 빔(SEO_FACT_EMPTY) — 다시 지어 보고, 끝까지 비면 그 까닭으로 실패

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`SEO 생성 시도 ${attempt}/${maxRetries}`);
        return await this.processAiSeo(product, options);
      } catch (error) {
        console.error(`SEO 생성 오류 (시도 ${attempt}):`, error);
        if (error && error.code === 'SEO_FACT_EMPTY') {
          factEmpty = error;
          continue;
        }

        const errorInfo = this.handleGeminiApiError(error);

        // Gemini API 에러로 프로세스 중단이 필요한 경우
        if (errorInfo.shouldStopProcess) {
          console.error('Gemini API 오류로 인해 프로세스를 중단합니다.');
          throw new Error(`GEMINI_API_ERROR: ${errorInfo.userMessage} — ${this.geminiErrorText(errorInfo.userMessage)}`);
        }

        if (!errorInfo.shouldRetry) {
          // 재시도할 수 없는 오류
          console.log('재시도할 수 없는 오류입니다. 기본값으로 진행합니다.');
          break;
        }

        if (errorInfo.isGeminiError) {
          geminiRetryCount++;
          if (geminiRetryCount >= 3) {
            console.log('Gemini API 재시도 한도 초과. 작업을 중단합니다.');
            throw new Error('Gemini API 재시도 한도 초과');
          }
        }

        if (attempt < maxRetries) {
          console.log(`${errorInfo.retryDelay / 1000}초 후 재시도합니다...`);
          await new Promise(resolve => setTimeout(resolve, errorInfo.retryDelay));
        }
      }
    }

    if (factEmpty) throw factEmpty;
    // 모든 재시도 실패 시 에러 throw (기본값 사용하지 않음)
    console.error('❌ 모든 SEO 재시도 실패. 에러 상태로 처리합니다.');
    throw new Error('SEO 생성 실패: 모든 재시도 실패');
  },

  /**
   * 기본 SEO 데이터 생성 (에러 상태에서 최소 정보 제공용)
   * @param {Object} product - 상품 객체
   * @returns {Object} 기본 SEO 데이터
   */
  getDefaultSeoData(product) {
    return {
      productName: product.productName || '상품명',
      keywords: [],
      metaDescription: '',
      features: [],
      score: 0,
      scoreBreakdown: [],
      isDefault: true, // 기본값 사용 플래그
      options: product.options?.map(option => ({
        optionNumber: option.optionNumber,
        korName: option.korName || option.optionName || '옵션명'
      })) || []
    };
  }
};

export default seoService;